import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Plus, ChevronRight, ChevronDown, Trash2,
  Diamond, Square, Circle, Zap, FileText, DollarSign, Layers, Settings2,
  PanelLeftClose, ChevronsUpDown, ChevronsDownUp, Minus, Calculator,
  CheckCircle, XCircle, Clock, Send, Shield, RotateCcw, AlertTriangle, User,
  FolderPlus, AlignJustify, List, Wrench, Sun, Moon, Copy, GitBranch, History, Eye
} from 'lucide-react';
import { BomThemeProvider, useBomTheme } from '../components/bom/BomThemeContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/bom-theme.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { DragProvider, useDragContext, DRAG_SOURCE, DROP_POSITION } from '../components/BomDragContext';
import { FabricationOpsSection } from '../components/FabricationOpsSection';
import { SurfaceTreatmentSection } from '../components/SurfaceTreatmentSection';
import { ManufacturingTemplateActions } from '../components/ManufacturingTemplateSection';

import { NestingSummaryPanel } from '../components/NestingSummaryPanel';
import { FastenerSelector } from '../components/bom/FastenerSelector';
import { FastenerSummaryPanel } from '../components/bom/FastenerSummaryPanel';
import { PurchaseBomButton } from '../components/bom/PurchaseBomPanel';
import { WeightAnalysisButton } from '../components/bom/WeightAnalysisPanel';
import { ActivityFeedPanel } from '../components/bom/ActivityFeedPanel';
import ProductBuilderFooter from '../components/bom/ProductBuilderFooter';
import ComingSoonLibrary from '../components/bom/ComingSoonLibrary';
import BatchDetailPanel from '../components/bom/BatchDetailPanel';
import RawMaterialLibraryPage from './RawMaterialLibraryPage';
import FastenersLibraryPage from './FastenersLibraryPage';
import ProcessLibraryPage from './ProcessLibraryPage';
import PartsListPage from './PartsListPage';
import { RMLibrarySelector } from '../components/RMLibrarySelector';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/* ------------------------------------------------------------------ */
/* Part Number with segment styling per Enhancement 2                  */
/* Segments: [MainGroup] [SubGroup] [RunningNumber] [Checksum]        */
/* RunningNumber is bold/bright, others are dimmer                     */
/* ------------------------------------------------------------------ */
const PartNumber = ({ display, compact, className = '' }) => {
  const text = display || compact || '';
  const parts = text.split(' ');
  if (parts.length === 4) {
    return (
      <span className={`font-mono ${className}`}>
        <span className="text-slate-500">{parts[0]}</span>{' '}
        <span className="text-slate-500">{parts[1]}</span>{' '}
        <span className="font-semibold text-cyan-300">{parts[2]}</span>{' '}
        <span className="text-slate-600">{parts[3]}</span>
      </span>
    );
  }
  return <span className={`font-mono ${className}`}>{text}</span>;
};

/* ------------------------------------------------------------------ */
/* Classification icons per UX spec                                    */
/* ------------------------------------------------------------------ */
const ClassIcon = ({ type, size = 14 }) => {
  const cls = {
    Buy: { color: '#059669', label: '●' },
    Make: { color: '#2563EB', label: '■' },
    Raw_Material: { color: '#D97706', label: '◇' },
    Consumable: { color: '#7C3AED', label: '○' },
  };
  const c = cls[type] || cls.Buy;
  return <span style={{ color: c.color, fontSize: size, fontWeight: 700, lineHeight: 1 }}>{c.label}</span>;
};

const AssemblyIcon = ({ size = 14 }) => (
  <span style={{ color: '#2563EB', fontSize: size, fontWeight: 700, lineHeight: 1 }}>◆</span>
);

const ClassBadge = ({ type, isAssembly }) => {
  const colors = {
    Buy: 'bg-emerald-900/30 text-emerald-400 border-emerald-600/30',
    Make: 'bg-blue-900/30 text-blue-400 border-blue-600/30',
    Raw_Material: 'bg-amber-900/30 text-amber-400 border-amber-600/30',
    Consumable: 'bg-violet-900/30 text-violet-400 border-violet-600/30',
  };
  const label = isAssembly ? 'Assy' : type?.replace('_', ' ');
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${colors[type] || ''}`}>{label}</span>;
};

/* ------------------------------------------------------------------ */
/* Indian number formatter                                             */
/* ------------------------------------------------------------------ */
const formatINR = (v) => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';

/* ------------------------------------------------------------------ */
/* Budget Progress Bar (reusable for cost & weight)                    */
/* ------------------------------------------------------------------ */
const BudgetProgressBar = ({ current, max, colorScheme = 'emerald' }) => {
  const pct = max > 0 ? Math.round((current / max) * 100) : 0;
  const barColor = pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : (colorScheme === 'blue' ? 'bg-blue-500' : 'bg-emerald-500');
  const textColor = pct > 100 ? 'text-red-400' : pct > 80 ? 'text-amber-400' : (colorScheme === 'blue' ? 'text-blue-400' : 'text-emerald-400');
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-slate-700 rounded-full overflow-hidden relative">
        <div className="absolute h-full w-0.5 bg-amber-400/40" style={{ left: '80%' }} />
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`font-mono font-semibold text-[10px] ${textColor}`}>{pct}%</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Budget Settings Dialog                                              */
/* ------------------------------------------------------------------ */
const BudgetSettingsDialog = ({ bomId, costBudget, weightBudget, description: initialDescription, onSave, onClose }) => {
  const [costVal, setCostVal] = useState(costBudget || '');
  const [weightVal, setWeightVal] = useState(weightBudget || '');
  const [descVal, setDescVal] = useState(initialDescription || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const tk = getToken();
    try {
      const body = { reason: reason || undefined };
      if (costVal !== '' && Number(costVal) !== costBudget) body.budget_target = parseFloat(costVal);
      if (weightVal !== '' && Number(weightVal) !== weightBudget) body.weight_budget_kg = parseFloat(weightVal);
      if (descVal !== (initialDescription || '')) body.description = descVal;
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/budget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Budget updated');
        onSave?.();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="px-5 pb-3 pt-2 border-t border-slate-700/30 animate-in fade-in duration-200 bg-slate-800/80">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold flex items-center gap-1">
          <Settings2 className="w-3 h-3" /> Budget Settings
        </span>
        <button onClick={onClose} className="text-slate-500 hover:text-white text-xs">✕</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-slate-500 mb-1 block">Cost Budget (₹)</label>
          <input type="number" value={costVal} onChange={e => setCostVal(e.target.value)}
            className="w-full h-7 text-xs bg-slate-900 border border-slate-600 text-white font-mono rounded px-2 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none"
            min="0" step="1000" placeholder="e.g., 650000" data-testid="budget-cost-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 mb-1 block">Weight Budget (kg)</label>
          <input type="number" value={weightVal} onChange={e => setWeightVal(e.target.value)}
            className="w-full h-7 text-xs bg-slate-900 border border-slate-600 text-white font-mono rounded px-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            min="0" step="1" placeholder="e.g., 250" data-testid="budget-weight-input" />
        </div>
      </div>
      <div className="mt-2">
        <label className="text-[10px] text-slate-500 mb-1 block">Product Description</label>
        <input type="text" value={descVal} onChange={e => setDescVal(e.target.value)}
          className="w-full h-7 text-xs bg-slate-900 border border-slate-600 text-white rounded px-2 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none"
          placeholder="e.g., Liquid Cooling Unit 80kW" data-testid="budget-description-input" />
      </div>
      <div className="mt-2">
        <label className="text-[10px] text-slate-500 mb-1 block">Reason (optional)</label>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)}
          className="w-full h-7 text-xs bg-slate-900 border border-slate-700 text-white rounded px-2 focus:border-slate-500 focus:outline-none"
          placeholder="Reason for change..." />
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={handleSave} disabled={saving}
          className="px-3 py-1 text-[10px] bg-blue-600 hover:bg-blue-500 text-white rounded font-medium disabled:opacity-50"
          data-testid="budget-save-btn">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} className="px-3 py-1 text-[10px] bg-slate-700 hover:bg-slate-600 text-slate-300 rounded">Cancel</button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Cost & Weight Bar                                                   */
/* ------------------------------------------------------------------ */
const CostBar = ({ cost, budget, weight, weightBudget, bomId, revisionId, description, onRefresh }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showNesting, setShowNesting] = useState(false);
  const [showFasteners, setShowFasteners] = useState(false);
  const [showBudgetSettings, setShowBudgetSettings] = useState(false);
  const pct = budget > 0 ? Math.round((cost / budget) * 100) : 0;
  const textColor = pct > 100 ? 'text-red-400' : pct > 80 ? 'text-amber-400' : 'text-emerald-400';
  const bgZone = pct > 100 ? 'bg-red-500/5' : pct > 80 ? 'bg-amber-500/5' : '';

  const wPct = weightBudget > 0 ? Math.round(((weight || 0) / weightBudget) * 100) : 0;
  const wTextColor = wPct > 100 ? 'text-red-400' : wPct > 80 ? 'text-amber-400' : 'text-blue-400';

  useEffect(() => {
    if (showBreakdown && bomId && revisionId && !breakdown) {
      (async () => {
        const tk = getToken();
        try {
          const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revisionId}/cost`, { headers: { Authorization: `Bearer ${tk}` } });
          if (res.ok) setBreakdown(await res.json());
        } catch (e) { console.error(e); }
      })();
    }
  }, [showBreakdown, bomId, revisionId, breakdown]);
  useEffect(() => { setBreakdown(null); }, [cost]);

  const closeAllPanels = (except) => {
    if (except !== 'breakdown') setShowBreakdown(false);
    if (except !== 'nesting') setShowNesting(false);
    if (except !== 'fasteners') setShowFasteners(false);
    if (except !== 'budget') setShowBudgetSettings(false);
  };

  return (
    <div className={`border-b border-slate-700/50 ${bgZone}`} data-testid="cost-bar">
      <div className="flex items-center gap-4 px-5 py-1.5 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Budget:</span>
          <span className="font-mono text-slate-300">{budget ? `₹${formatINR(budget)}` : '—'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">BOM Cost:</span>
          <span className={`font-mono font-semibold ${textColor}`}>₹{formatINR(cost)}</span>
        </div>
        {budget > 0 && <BudgetProgressBar current={cost} max={budget} colorScheme="emerald" />}

        <div className="flex items-center gap-1.5 border-l border-slate-700/50 pl-4">
          <span className="text-slate-500">Wt Budget:</span>
          <span className="font-mono text-slate-300">{weightBudget ? `${weightBudget} kg` : '—'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">BOM Wt:</span>
          <span className={`font-mono font-semibold ${wTextColor}`}>{(weight || 0).toFixed(3)} kg</span>
        </div>
        {weightBudget > 0 && <BudgetProgressBar current={weight || 0} max={weightBudget} colorScheme="blue" />}

        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => { const next = !showBudgetSettings; closeAllPanels('budget'); setShowBudgetSettings(next); }}
            className={`px-1.5 py-0.5 text-[10px] rounded transition-colors flex items-center gap-1 ${showBudgetSettings ? 'bg-slate-600/60 text-white' : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white'}`}
            data-testid="cost-bar-budget-settings-toggle">
            <Settings2 className="w-3 h-3" />
          </button>
          <button onClick={() => { const next = !showBreakdown; closeAllPanels('breakdown'); setShowBreakdown(next); }}
            className={`px-2 py-0.5 text-[10px] rounded transition-colors flex items-center gap-1 ${showBreakdown ? 'bg-slate-600/60 text-white' : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white'}`}
            data-testid="cost-bar-breakdown-toggle">
            <DollarSign className="w-3 h-3" />{showBreakdown ? 'Hide' : 'Show'} Breakdown
          </button>
          <button onClick={() => { const next = !showNesting; closeAllPanels('nesting'); setShowNesting(next); }}
            className={`px-2 py-0.5 text-[10px] rounded transition-colors flex items-center gap-1 ${showNesting ? 'bg-cyan-600/40 text-cyan-300' : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white'}`}
            data-testid="cost-bar-nesting-toggle">
            <Layers className="w-3 h-3" />{showNesting ? 'Hide' : 'Show'} Nesting
          </button>
          <button onClick={() => { const next = !showFasteners; closeAllPanels('fasteners'); setShowFasteners(next); }}
            className={`px-2 py-0.5 text-[10px] rounded transition-colors flex items-center gap-1 ${showFasteners ? 'bg-amber-600/40 text-amber-300' : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white'}`}
            data-testid="cost-bar-fasteners-toggle">
            <Wrench className="w-3 h-3" />{showFasteners ? 'Hide' : 'Show'} Fasteners
          </button>
          <PurchaseBomButton bomId={bomId} revisionId={revisionId} />
          <WeightAnalysisButton bomId={bomId} revisionId={revisionId} />
        </div>
      </div>
      {showBudgetSettings && (
        <BudgetSettingsDialog bomId={bomId} costBudget={budget} weightBudget={weightBudget} description={description}
          onSave={onRefresh} onClose={() => setShowBudgetSettings(false)} />
      )}
      {showBreakdown && breakdown && (
        <div className="px-5 pb-3 pt-1 border-t border-slate-700/30 animate-in fade-in duration-200">
          <div className="flex gap-4 text-[11px] flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-slate-500">Buy:</span>
              <span className="font-mono text-blue-300">₹{breakdown.breakdown.buy_parts.total.toLocaleString()}</span>
              <span className="text-slate-600">({breakdown.breakdown.buy_parts.percentage}%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-slate-500">Material:</span>
              <span className="font-mono text-emerald-300">₹{breakdown.breakdown.make_raw_material.total.toLocaleString()}</span>
              <span className="text-slate-600">({breakdown.breakdown.make_raw_material.percentage}%)</span>
            </div>
            {breakdown.breakdown.make_fabrication && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-400" />
                <span className="text-slate-500">Fabrication:</span>
                <span className="font-mono text-orange-300">₹{breakdown.breakdown.make_fabrication.total.toLocaleString()}</span>
                <span className="text-slate-600">({breakdown.breakdown.make_fabrication.percentage}%)</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-violet-400" />
              <span className="text-slate-500">Treatment:</span>
              <span className="font-mono text-violet-300">₹{breakdown.breakdown.process_consumables.total.toLocaleString()}</span>
              <span className="text-slate-600">({breakdown.breakdown.process_consumables.percentage}%)</span>
            </div>
            {breakdown.breakdown.total_weight_kg != null && (
              <div className="flex items-center gap-2 border-l border-slate-700/50 pl-3">
                <span className="text-slate-500">Weight:</span>
                <span className="font-mono text-blue-300">{breakdown.breakdown.total_weight_kg} kg</span>
                {breakdown.breakdown.make_weight_kg > 0 && (
                  <span className="text-slate-600 text-[10px]">
                    (Make: {breakdown.breakdown.make_weight_kg}kg
                    {breakdown.breakdown.buy_weight_kg > 0 && <> · Buy: {breakdown.breakdown.buy_weight_kg}kg</>})
                  </span>
                )}
              </div>
            )}
            <span className={`ml-auto px-2 py-0.5 rounded text-[10px] font-medium ${
              breakdown.budget_zone === 'over_budget' ? 'bg-red-500/20 text-red-300' :
              breakdown.budget_zone === 'warning' ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'
            }`}>{breakdown.budget_zone === 'over_budget' ? 'Over Budget' : breakdown.budget_zone === 'warning' ? 'Warning' : 'Healthy'}</span>
          </div>
        </div>
      )}
      {showNesting && <NestingSummaryPanel bomId={bomId} revisionId={revisionId} />}
      {showFasteners && <FastenerSummaryPanel bomId={bomId} revisionId={revisionId} />}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Tree scroll container — wires DragContext ref for edge scrolling     */
/* ------------------------------------------------------------------ */
const TreeScrollContainer = ({ children }) => {
  const drag = useDragContext();
  return (
    <div className="flex-1 overflow-y-auto" ref={drag?.treeContainerRef} data-testid="bom-tree-scroll-container">
      {children}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* BOM Tree Node — Controlled expand state from parent                 */
/* ------------------------------------------------------------------ */
const TreeNode = ({ line, lines, selected, onSelect, depth = 0, expandedMap, onToggleExpand, insertionParentId, treeViewMode = 'compact', isLastChild = false, ancestorContinues = [], selectedLineIds, onToggleLineSelect, isDraft }) => {
  const children = lines.filter(l => l.parent_line_id === line.line_id);
  const hasRealChildren = children.length > 0;
  const isAssembly = line.is_assembly;
  const derived = line.derived;
  const hasDerivedConsumables = derived && derived.process_consumables?.length > 0;
  const hasFabricationOps = (line.fabrication_operations?.length || 0) > 0;
  const hasAppliedRules = (line.applied_process_rules?.length || 0) > 0;
  const hasMakeSubItems = (derived && (derived.material_cost || derived.sheet_cost || derived.stock_fraction)) || hasFabricationOps || hasAppliedRules;
  const hasChildren = hasRealChildren || hasMakeSubItems;
  const isExpanded = expandedMap[line.line_id] !== false; // default true
  const isSelected = selected === line.line_id;
  const isChecked = selectedLineIds?.has(line.line_id) || false;
  const showCheckbox = isDraft && !isAssembly;
  const isInsertionTarget = insertionParentId === line.line_id;
  const descRef = useRef(null);
  const rowRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimer = useRef(null);
  const drag = useDragContext();

  // Obs 2: part_name = primary identifier shown in tree. part_description = supplementary detail shown on expand.
  // Fallback: old lines won't have part_name — use part_description.
  const partName = isAssembly
    ? (line.assembly_name || line.part_description)
    : (line.part_name || line.part_description);
  const partDescription = isAssembly
    ? (line.part_description || '')
    : (line.part_description || '');
  // Only show description on expand when it adds information (differs from name)
  const hasDistinctDescription = partDescription && partDescription !== partName;
  const partLabel = isAssembly ? (line.assembly_number || line.part_id) : (line.part_number || line.part_id);
  // Smart truncation: shorten part number display at deeper levels
  const partNumTruncLen = depth >= 6 ? 8 : depth >= 4 ? 12 : null;
  const displayPartNum = partNumTruncLen && partLabel.length > partNumTruncLen
    ? partLabel.slice(0, partNumTruncLen) + '…' : null;

  const costText = (() => {
    if (line.make_buy === 'Make' && !line.is_assembly) {
      const matCost = derived?.material_cost || derived?.sheet_cost || 0;
      const fabCost = line.fabrication_cost_total || 0;
      const appliedRules = line.applied_process_rules || [];
      const treatCost = appliedRules.reduce((s, r) => s + (r.is_selected ? (r.cost || 0) : 0), 0);
      const unitCost = matCost + fabCost + treatCost;
      const qty = line.quantity || 1;
      return unitCost > 0 ? `₹${Math.round(qty * unitCost).toLocaleString()}` : '';
    }
    return line.unit_cost > 0 ? `₹${(line.quantity * line.unit_cost).toLocaleString()}` : '';
  })();

  // Weight per line: from derived (Make) or catalog (Buy/Consumable)
  const weightText = (() => {
    const qty = line.quantity || 1;
    const unitWt = derived?.weight_kg || line.unit_weight_kg || 0;
    if (unitWt > 0) {
      const total = qty * unitWt;
      return total >= 1 ? `${total.toFixed(2)} kg` : `${(total * 1000).toFixed(0)} g`;
    }
    return '';
  })();

  const fullTooltipText = `${partLabel} — ${partName || ''}${hasDistinctDescription ? ` (${partDescription})` : ''}${costText ? ` │ ${costText}` : ''}${weightText ? ` │ ${weightText}` : ''}`;

  const handleMouseEnter = () => {
    const el = descRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      tooltipTimer.current = setTimeout(() => setShowTooltip(true), 300);
    }
  };
  const handleMouseLeave = () => {
    if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    setShowTooltip(false);
  };

  const isComfortable = treeViewMode === 'comfortable';
  const rowHeight = isComfortable ? 'py-2' : 'py-1.5';

  // Tree connector: flags for children
  const childAncestorContinues = useMemo(
    () => [...ancestorContinues, !isLastChild],
    [ancestorContinues, isLastChild]
  );

  // Drag-and-drop: drop zone detection
  const isDropTarget = drag?.isDragging && drag.dropTarget === line.line_id;
  const isDropBefore = isDropTarget && drag.dropPosition === DROP_POSITION.BEFORE;
  const isDropAfter = isDropTarget && drag.dropPosition === DROP_POSITION.AFTER;
  const isDropChild = isDropTarget && drag.dropPosition === DROP_POSITION.CHILD;
  const isDropInvalid = isDropTarget && !drag.dropValid;
  const isDragSource = drag?.isDragging && drag.dragState?.type === DRAG_SOURCE.TREE_NODE && drag.dragState?.data?.line_id === line.line_id;

  const handleDragSourceStart = (e) => {
    e.stopPropagation();
    if (drag) drag.startDrag({ type: DRAG_SOURCE.TREE_NODE, data: line }, e);
  };

  const handleDropZone = (e) => {
    if (!drag?.isDragging || !rowRef.current) return;
    const rect = rowRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const ratio = y / rect.height;
    
    if (isAssembly || line.make_buy === 'Make') {
      // Assemblies and Make parts can be parents: 3-zone (before / child / after)
      if (ratio < 0.25) drag.setDropInfo(line.line_id, DROP_POSITION.BEFORE);
      else if (ratio > 0.75) drag.setDropInfo(line.line_id, DROP_POSITION.AFTER);
      else drag.setDropInfo(line.line_id, DROP_POSITION.CHILD);
    } else {
      // Buy/Consumable: 2-zone (before / after)
      if (ratio < 0.5) drag.setDropInfo(line.line_id, DROP_POSITION.BEFORE);
      else drag.setDropInfo(line.line_id, DROP_POSITION.AFTER);
    }
  };

  // Compute tree line positions
  const rootLineX = 7;
  const lineX = (lvl) => 19 + lvl * 20;
  const parentLineX = depth === 0 ? rootLineX : lineX(depth - 1);
  const branchWidth = depth === 0 ? 7 : 12;

  return (
    <div className="relative" data-tree-depth={depth}>
      {/* Tree guide lines at container level (span full height) */}
      {ancestorContinues.map((continues, i) => continues && (
        <div key={`ag-${i}`} className="absolute top-0 bottom-0 w-px bom-tree-line pointer-events-none z-[1]"
          style={{ left: `${lineX(i)}px` }} />
      ))}
      <div className="absolute w-px bom-tree-line pointer-events-none z-[1]"
        style={{ left: `${parentLineX}px`, top: 0, height: isLastChild ? '14px' : '100%' }} />

      {/* Drop indicator line: BEFORE this row */}
      {isDropBefore && !isDropInvalid && (
        <div className="h-0.5 bg-blue-500 mx-2 rounded-full" style={{ marginLeft: `${12 + depth * 20}px` }} />
      )}
      <div
        ref={rowRef}
        className={`flex ${isComfortable ? 'flex-wrap' : ''} items-center gap-1 ${rowHeight} px-2 cursor-pointer border-l-2 transition-all text-xs hover:bg-slate-700/30 relative group/row ${
          isDragSource ? 'opacity-40 border-l-dashed border-slate-500' :
          isDropChild && !isDropInvalid ? 'bg-blue-900/30 border-l-blue-400 ring-1 ring-blue-500/30' :
          isDropChild && isDropInvalid ? 'bg-red-900/20 border-l-red-400' :
          isSelected ? 'bg-blue-900/20 border-l-blue-500' :
          isInsertionTarget ? 'bg-cyan-900/10 border-l-cyan-500/50' :
          'border-l-transparent'
        }`}
        style={{ paddingLeft: `${12 + depth * 20}px` }}
        onClick={() => onSelect(line.line_id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={(e) => { handleMouseLeave(e); if (drag?.isDragging) drag.setDropInfo(null, DROP_POSITION.NONE); }}
        onMouseMove={handleDropZone}
        data-testid={`tree-node-${line.line_id}`}
      >
        {/* Horizontal branch connector (inside row for vertical centering) */}
        <div className="absolute h-px bom-tree-line pointer-events-none z-[1]"
          style={{ left: `${parentLineX}px`, top: '50%', width: `${branchWidth}px` }} />
        {hasChildren ? (
          <button onClick={e => { e.stopPropagation(); onToggleExpand(line.line_id); }} className="p-0.5 text-slate-500 hover:text-white shrink-0">
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-4 shrink-0" />}
        {/* Drag handle for tree rows */}
        <span
          className="shrink-0 cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 opacity-0 group-hover/row:opacity-100 transition-opacity text-[10px] select-none"
          onMouseDown={handleDragSourceStart}
          title="Drag to reorder"
        >⠿</span>
        {showCheckbox && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleLineSelect?.(line.line_id, e); }}
            className={`shrink-0 w-3.5 h-3.5 rounded-sm border flex items-center justify-center transition-colors ${
              isChecked
                ? 'bg-cyan-600 border-cyan-500 text-white'
                : 'bg-transparent border-slate-600 opacity-0 group-hover/row:opacity-100 hover:border-cyan-500'
            }`}
            data-testid={`tree-checkbox-${line.line_id}`}
            title="Select for batch operations"
          >
            {isChecked && <span className="text-[8px] font-bold leading-none">✓</span>}
          </button>
        )}
        <span className="shrink-0">{isAssembly ? <AssemblyIcon size={12} /> : <ClassIcon type={line.make_buy} size={12} />}</span>
        <span className="shrink-0" title={displayPartNum ? partLabel : undefined}>
          {isAssembly ? (
            <span className="font-mono font-semibold text-cyan-300 text-[11px]">{displayPartNum || partLabel}</span>
          ) : (
            <PartNumber display={displayPartNum || line.part_number} compact={displayPartNum || line.part_id} className="text-[11px]" />
          )}
        </span>
        <span ref={descRef} className="text-slate-500 truncate flex-1 min-w-0">{partName}</span>
        {weightText && <span className="font-mono text-blue-400/70 whitespace-nowrap shrink-0 text-[10px]">{weightText}</span>}
        <span className="font-mono text-slate-400 whitespace-nowrap shrink-0 ml-1">{costText}</span>
        <span className="shrink-0"><ClassBadge type={line.make_buy} isAssembly={isAssembly} /></span>
        {hasDerivedConsumables && <Zap className="w-3 h-3 text-violet-400 shrink-0" title="Auto-derived" />}
        {/* Hover tooltip for truncated descriptions */}
        {showTooltip && (
          <div className="absolute left-4 top-full z-50 mt-0.5 max-w-[400px] px-2.5 py-1.5 bg-slate-800 border border-slate-600 rounded shadow-lg text-[11px] text-slate-200 whitespace-normal break-words pointer-events-none"
            style={{ marginLeft: `${depth * 20}px` }}>
            {fullTooltipText}
          </div>
        )}
        {/* Comfortable mode: second line with description (only if different from name) */}
        {isComfortable && hasDistinctDescription && (
          <div className="w-full text-[11px] text-slate-600 truncate pl-8 -mt-0.5"
            style={{ paddingLeft: `${20}px` }}>
            {partDescription}
          </div>
        )}
      </div>
      {/* Drop indicator line: AFTER this row */}
      {isDropAfter && !isDropInvalid && (
        <div className="h-0.5 bg-blue-500 mx-2 rounded-full" style={{ marginLeft: `${12 + depth * 20}px` }} />
      )}

      {isExpanded && children.map((child, idx) => (
        <TreeNode key={child.line_id} line={child} lines={lines} selected={selected}
          onSelect={onSelect} depth={depth + 1} expandedMap={expandedMap}
          onToggleExpand={onToggleExpand} insertionParentId={insertionParentId} treeViewMode={treeViewMode}
          isLastChild={idx === children.length - 1} ancestorContinues={childAncestorContinues}
          selectedLineIds={selectedLineIds} onToggleLineSelect={onToggleLineSelect} isDraft={isDraft} />
      ))}

      {isExpanded && hasMakeSubItems && (
        <>
          {/* Layer 1: Raw Material */}
          {derived?.sheets_required != null && (
            <div className="flex items-center gap-1 py-1 px-2 text-[11px] text-slate-500 bg-amber-900/5 border-l border-l-amber-600/30"
              style={{ paddingLeft: `${32 + depth * 20}px` }}>
              <span className="text-amber-500">◇</span>
              <span className="font-mono text-amber-400/80">{derived.sheets_required.toFixed(3)} sheets</span>
              <span className="text-slate-600">@</span>
              <span className="font-mono text-emerald-500/70">₹{derived.sheet_cost?.toLocaleString()}</span>
            </div>
          )}
          {derived?.material_cost != null && !derived?.sheets_required && (
            <div className="flex items-center gap-1 py-1 px-2 text-[11px] text-slate-500 bg-amber-900/5 border-l border-l-amber-600/30"
              style={{ paddingLeft: `${32 + depth * 20}px` }}>
              <span className="text-amber-500">◇</span>
              <span className="text-amber-300/70 truncate max-w-[120px]">
                {line.engineering_parameters?.material_profile_id ? 'Material' : 'Raw Material'}
              </span>
              {derived?.stock_fraction != null && <span className="font-mono text-slate-400">{derived.stock_fraction.toFixed(4)} frac</span>}
              <span className="font-mono text-emerald-500/70 ml-auto">₹{derived.material_cost.toLocaleString()}</span>
            </div>
          )}

          {/* Layer 2: Fabrication Operations */}
          {(line.fabrication_operations || []).map((op, i) => (
            <div key={`fab-${i}`} className="flex items-center gap-1 py-1 px-2 text-[11px] text-slate-500 bg-orange-900/5 border-l border-l-orange-600/20"
              style={{ paddingLeft: `${32 + depth * 20}px` }}>
              <span className="text-orange-400">⚙</span>
              <span className="text-orange-300/70">{op.operation_name}</span>
              <span className="font-mono text-orange-400/70 ml-auto">₹{op.operation_cost?.toLocaleString()}</span>
            </div>
          ))}

          {/* Layer 3: Surface Treatment — ONLY from explicitly applied rules */}
          {(line.applied_process_rules || []).map((item, i) => {
            if (!item.is_selected) return null;
            const name = item.rule_name || item.name;
            return (
              <div key={`trt-${i}`} className="flex items-center gap-1 py-1 px-2 text-[11px] text-slate-500 bg-violet-900/5 border-l border-l-violet-600/20"
                style={{ paddingLeft: `${32 + depth * 20}px` }}>
                <span className="text-violet-400">○</span>
                <span className="text-violet-300/70">{name}</span>
                <span className="font-mono text-slate-400">{(item.quantity || 0).toFixed(3)} {item.uom}</span>
                <span className="font-mono text-violet-400/70 ml-auto">₹{item.cost}</span>
              </div>
            );
          })}
        </>
      )}

      {/* Empty assembly placeholder */}
      {isExpanded && isAssembly && children.length === 0 && !hasMakeSubItems && (
        <div className="flex items-center gap-1.5 py-1.5 px-2 text-[10px] text-slate-600 italic"
          style={{ paddingLeft: `${32 + depth * 20}px` }}>
          (empty — add parts below)
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Search Result Card                                                  */
/* ------------------------------------------------------------------ */
const SearchCard = ({ part, onAdd, onPreview }) => {
  const drag = useDragContext();
  const isAssembly = part.part_type === 'assembly' && part.linked_bom_id;
  const handleDragStart = (e) => {
    if (!drag) return;
    drag.startDrag({ type: DRAG_SOURCE.SEARCH_CARD, data: part }, e);
  };

  return (
    <div className={`p-2.5 border rounded-lg hover:bg-slate-800/40 transition-all group ${
      isAssembly ? 'border-blue-700/40 bg-blue-950/10 hover:border-blue-600/50' : 'border-slate-700/50 hover:border-slate-600'
    } ${drag?.isDragging && drag.dragState?.data?.hpn_compact === part.hpn_compact ? 'opacity-40' : ''}`}
      data-testid={`search-card-${part.hpn_compact}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0">
          {isAssembly && <span className="text-blue-400 font-bold text-sm shrink-0">◆</span>}
          <PartNumber display={part.hpn_display} compact={part.hpn_compact} className="text-xs" />
        </div>
        <span className="text-[10px] text-slate-500 shrink-0">{part.uom || 'EA'}</span>
      </div>
      <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{part.description}</div>
      {part.manufacturer_name && (
        <div className="text-[10px] text-slate-500 mt-0.5">OEM: <span className="text-slate-400">{part.manufacturer_name}</span></div>
      )}
      {part.mpn && <div className="text-[10px] text-slate-600 mt-0.5">MPN: {part.mpn}</div>}
      {isAssembly && (
        <div className="text-[10px] text-blue-400/80 mt-1 flex items-center gap-1.5 bg-blue-900/15 rounded px-1.5 py-0.5">
          <Layers className="w-3 h-3 text-blue-500" />
          <span>Sub-Assembly</span>
          {part.child_count != null && <span className="text-slate-500">│ {part.child_count} parts</span>}
          {part.bom_status && <span className={`text-[9px] ${part.bom_status === 'Released' ? 'text-emerald-400' : 'text-slate-500'}`}>│ {part.bom_status}</span>}
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <Badge variant="outline" className={`text-[10px] ${
            isAssembly ? 'border-blue-600/40 text-blue-400' : 'border-slate-600 text-slate-400'
          }`}>
            {isAssembly ? 'Assembly' : part.part_type === 'make' ? 'Make' : part.subgroup_name || part.category || 'Part'}
          </Badge>
          {!isAssembly && part.main_group_name && <span className="text-[9px] text-slate-600">{part.main_group_name}</span>}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isAssembly && onPreview && (
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-blue-400 hover:bg-blue-900/20"
              onClick={() => onPreview(part)} data-testid={`preview-assembly-${part.hpn_compact}`}>
              <ChevronRight className="w-3 h-3" /> Preview
            </Button>
          )}
          <span
            className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 px-1 py-0.5 text-[12px] select-none"
            onMouseDown={handleDragStart}
            title="Drag to BOM tree"
            data-testid={`drag-handle-${part.hpn_compact}`}
          >⠿</span>
          <Button size="sm" variant="ghost" className={`h-6 px-2 text-[10px] hover:bg-blue-900/20 ${isAssembly ? 'text-blue-400' : 'text-blue-400'}`}
            onClick={() => onAdd(part)} data-testid={`add-part-${part.hpn_compact}`}>
            <Plus className="w-3 h-3 mr-0.5" /> {isAssembly ? 'Add Assy' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Breadcrumb Bar — Shows path from root to selected node              */
/* ------------------------------------------------------------------ */
const BreadcrumbBar = ({ lines, selectedLineId, productName, onSelect }) => {
  const path = useMemo(() => {
    if (!selectedLineId) return [];
    const result = [];
    let current = lines.find(l => l.line_id === selectedLineId);
    while (current) {
      result.unshift(current);
      current = lines.find(l => l.line_id === current.parent_line_id);
    }
    return result;
  }, [lines, selectedLineId]);

  if (path.length === 0) return null;

  return (
    <div className="flex items-center gap-1 px-3 py-1 text-[10px] text-slate-500 bg-slate-800/30 border-b border-slate-700/30 overflow-x-auto">
      <button onClick={() => onSelect(null)} className="text-cyan-400 hover:text-cyan-300 whitespace-nowrap">{productName}</button>
      {path.map((node, i) => (
        <React.Fragment key={node.line_id}>
          <ChevronRight className="w-3 h-3 text-slate-600 shrink-0" />
          <button
            onClick={() => onSelect(node.line_id)}
            className={`whitespace-nowrap hover:text-slate-300 ${i === path.length - 1 ? 'text-white font-medium' : 'text-slate-400'}`}
          >
            {node.is_assembly
              ? `${node.assembly_number || node.part_id} (${node.assembly_name || node.part_name || node.part_description})`
              : (node.part_number || node.part_id)}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Detail Panel                                                        */
/* ------------------------------------------------------------------ */
const DetailPanel = ({ line, profiles, onUpdate, onRemove, budgetContext = {}, bomId, revisionId, onRefresh, isDraft = true, onProfileCreated, allLines = [], onBusy }) => {
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [whatIfMode, setWhatIfMode] = useState(false);
  const [whatIfPreview, setWhatIfPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [rmAdaptedProfile, setRmAdaptedProfile] = useState(null);
  const saveTimerRef = useRef(null);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    if (line) {
      const isBuyLike = !line.is_assembly && line.make_buy !== 'Make';
      setEditValues({
        quantity: line.quantity,
        find_number: line.find_number,
        reference_designator: line.reference_designator || '',
        assembly_name: line.is_assembly ? (line.assembly_name || line.part_description || '') : '',
        part_name: line.part_name || '',
        part_description: (!line.is_assembly && line.make_buy === 'Make') ? (line.part_description || '') : '',
        surface_area_m2: line.engineering_parameters?.surface_area_m2 || '',
        length_m: line.engineering_parameters?.length_m || '',
        material_profile_id: line.engineering_parameters?.material_profile_id || '',
        // Buy/RM/Fastener inline editing fields
        unit_cost: isBuyLike ? (line.unit_cost || '') : '',
        unit_weight_kg: isBuyLike ? (line.unit_weight_kg || '') : '',
        lead_time_days: isBuyLike ? (line.lead_time_days || '') : '',
      });
      setWhatIfMode(false);
      setWhatIfPreview(null);
    }
  }, [line?.line_id, line?.quantity, line?.unit_cost, line?.unit_weight_kg, line?.lead_time_days,
      line?.engineering_parameters?.surface_area_m2,
      line?.engineering_parameters?.length_m, line?.engineering_parameters?.material_profile_id]);

  const fetchWhatIfPreview = async (values, force = false) => {
    if (!line || line.make_buy !== 'Make' || line.is_assembly) return;
    const sa = parseFloat(values.surface_area_m2) || 0;
    const lm = parseFloat(values.length_m) || 0;
    const profileId = values.material_profile_id;
    if (!profileId || (sa <= 0 && lm <= 0)) { setWhatIfPreview(null); return; }
    // Skip if nothing changed (unless force=true for manual invocation)
    if (!force) {
      const currentSa = line.engineering_parameters?.surface_area_m2 || 0;
      const currentLm = line.engineering_parameters?.length_m || 0;
      const currentProfile = line.engineering_parameters?.material_profile_id || '';
      if (sa === currentSa && lm === currentLm && profileId === currentProfile) { setWhatIfMode(false); setWhatIfPreview(null); return; }
    }

    setLoadingPreview(true);
    setWhatIfMode(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/bom/simulate-cost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          material_profile_id: profileId,
          surface_area_m2: sa,
          length_m: lm,
          current_derived: line.derived,
          applied_process_rules: line.applied_process_rules || [],
          fabrication_cost_total: line.fabrication_cost_total || 0,
          current_fabrication_cost: line.fabrication_cost_total || 0,
          budget_context: budgetContext,
        }),
      });
      if (res.ok) setWhatIfPreview(await res.json());
    } catch (e) { console.error(e); }
    setLoadingPreview(false);
  };

  // Manual preview — always fetches, bypasses "no change" guard
  const handleManualPreview = () => fetchWhatIfPreview(editValues, true);

  const handleFieldChange = (field, value) => {
    const newValues = { ...editValues, [field]: value };
    setEditValues(newValues);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (line?.make_buy === 'Make' && !line.is_assembly && (field === 'surface_area_m2' || field === 'material_profile_id' || field === 'length_m')) {
      previewTimerRef.current = setTimeout(() => fetchWhatIfPreview(newValues), 300);
    }
  };

  const handleSave = async (values, forceRederive = false) => {
    if (!line) return;
    setSaving(true);
    const updates = {};
    if (values.quantity !== line.quantity) updates.quantity = parseFloat(values.quantity) || line.quantity;
    if (values.reference_designator !== (line.reference_designator || '')) updates.reference_designator = values.reference_designator;
    if (line.is_assembly && values.assembly_name !== (line.assembly_name || line.part_description || '')) updates.assembly_name = values.assembly_name;
    if (!line.is_assembly && line.make_buy === 'Make' && values.part_description !== (line.part_description || '')) updates.part_description = values.part_description;
    // Part name update (Make parts only for now)
    if (!line.is_assembly && line.make_buy === 'Make' && values.part_name !== (line.part_name || '')) updates.part_name = values.part_name;
    if (line.make_buy === 'Make' && !line.is_assembly) {
      const sa = parseFloat(values.surface_area_m2);
      if (!isNaN(sa) && sa !== (line.engineering_parameters?.surface_area_m2 || 0)) updates.surface_area_m2 = sa;
      const lm = parseFloat(values.length_m);
      if (!isNaN(lm) && lm !== (line.engineering_parameters?.length_m || 0)) updates.length_m = lm;
      if (values.material_profile_id && values.material_profile_id !== (line.engineering_parameters?.material_profile_id || '')) updates.material_profile_id = values.material_profile_id;
      if (forceRederive && values.material_profile_id && !updates.material_profile_id) {
        updates.material_profile_id = values.material_profile_id;
      }
    }
    // Buy/RM/Fastener inline cost/weight/lead_time editing
    if (!line.is_assembly && line.make_buy !== 'Make') {
      const newCost = parseFloat(values.unit_cost);
      if (!isNaN(newCost) && newCost !== (line.unit_cost || 0)) updates.unit_cost = newCost;
      const newWeight = parseFloat(values.unit_weight_kg);
      if (!isNaN(newWeight) && newWeight !== (line.unit_weight_kg || 0)) updates.unit_weight_kg = newWeight;
      const newLead = parseFloat(values.lead_time_days);
      if (!isNaN(newLead) && newLead !== (line.lead_time_days || 0)) updates.lead_time_days = newLead;
    }
    if (Object.keys(updates).length > 0) {
      await onUpdate(line.line_id, updates);
      setWhatIfMode(false);
      setWhatIfPreview(null);
    } else if (forceRederive) {
      if (values.material_profile_id) {
        await onUpdate(line.line_id, { material_profile_id: values.material_profile_id });
      }
      setWhatIfMode(false);
      setWhatIfPreview(null);
    }
    setSaving(false);
  };

  const handleApplyChanges = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    // When applying from What-If Preview, force re-derivation even if no fields changed.
    // The underlying RM price may have changed — sending material_profile_id forces backend to recompute derived values.
    if (whatIfMode && line?.make_buy === 'Make' && !line.is_assembly && editValues.material_profile_id) {
      handleSave(editValues, true);
    } else {
      handleSave(editValues);
    }
  };

  const handleCancelChanges = () => {
    const isBuyLike = !line.is_assembly && line.make_buy !== 'Make';
    setEditValues({
      quantity: line.quantity, find_number: line.find_number,
      reference_designator: line.reference_designator || '',
      assembly_name: line.is_assembly ? (line.assembly_name || line.part_description || '') : '',
      part_name: line.part_name || '',
      part_description: (!line.is_assembly && line.make_buy === 'Make') ? (line.part_description || '') : '',
      surface_area_m2: line.engineering_parameters?.surface_area_m2 || '',
      length_m: line.engineering_parameters?.length_m || '',
      material_profile_id: line.engineering_parameters?.material_profile_id || '',
      unit_cost: isBuyLike ? (line.unit_cost || '') : '',
      unit_weight_kg: isBuyLike ? (line.unit_weight_kg || '') : '',
      lead_time_days: isBuyLike ? (line.lead_time_days || '') : '',
    });
    setWhatIfMode(false);
    setWhatIfPreview(null);
  };

  const handleBlur = () => {
    if (whatIfMode) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    handleSave(editValues);
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !whatIfMode) e.target.blur(); };

  // Live material cost — computed from CURRENT profile data + engineering params.
  // Must be before any conditional returns (React hooks rule).
  const LINEAR_FORMS = new Set(['Pipe', 'Round_Bar', 'Hex_Bar', 'Square_Bar', 'Flat_Bar', 'Angle', 'Channel', 'Beam', 'Wire', 'Coil', 'Strip']);
  const oldProfile = line ? profiles.find(p => p.profile_id === editValues.material_profile_id) : null;
  const selectedProfile = oldProfile || rmAdaptedProfile;
  const isLinearProfile = selectedProfile && (selectedProfile.primary_input === 'length' || LINEAR_FORMS.has(selectedProfile.material_form));
  const liveMaterialCost = useMemo(() => {
    if (!line || !selectedProfile) return null;
    const costPerUnit = selectedProfile.cost_per_stock_unit || 0;
    if (isLinearProfile) {
      const lm = parseFloat(editValues.length_m) || 0;
      if (lm <= 0) return null;
      const stockLen = selectedProfile.stock_length_mm || 6000;
      const kerf = selectedProfile.kerf_loss_mm || 0;
      const stockFraction = stockLen > 0 ? ((lm * 1000) + kerf) / stockLen : 0;
      return costPerUnit > 0 ? Math.round(stockFraction * costPerUnit * 100) / 100 : 0;
    } else {
      const sa = parseFloat(editValues.surface_area_m2) || 0;
      if (sa <= 0) return null;
      const usableArea = selectedProfile.stock_usable_area_m2 || 0;
      const bufferPct = selectedProfile.nesting_buffer_pct || 30;
      if (usableArea <= 0) return null;
      const sheetsReq = sa / usableArea * (1 + bufferPct / 100);
      return costPerUnit > 0 ? Math.round(sheetsReq * costPerUnit * 100) / 100 : 0;
    }
  }, [line, selectedProfile, isLinearProfile, editValues.length_m, editValues.surface_area_m2]);

  if (!line) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-500 text-xs gap-2">
      <FileText className="w-8 h-8 text-slate-700" />
      <span>Select a part in the tree</span>
    </div>
  );

  const derived = line.derived;
  const isMakeNonAssembly = line.make_buy === 'Make' && !line.is_assembly;

  return (
    <div className="p-4 space-y-4 text-xs overflow-y-auto h-full">
      <div>
        <div className="flex items-center gap-2">
          {line.is_assembly ? <AssemblyIcon size={16} /> : <ClassIcon type={line.make_buy} size={16} />}
          {line.is_assembly ? (
            <span className="font-mono font-bold text-sm text-cyan-300">{line.assembly_number || line.part_id}</span>
          ) : (
            <PartNumber display={line.part_number} compact={line.part_id} className="text-sm" />
          )}
          {saving && <span className="text-[10px] text-blue-400 animate-pulse">Saving...</span>}
          {line.is_assembly && <Badge className="bg-blue-900/30 text-blue-400 border-blue-600/30 text-[9px]">Assembly</Badge>}
        </div>
        <div className="text-slate-400 mt-0.5">
          {line.is_assembly ? (
            <input
              type="text"
              value={editValues.assembly_name || ''}
              onChange={e => handleFieldChange('assembly_name', e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className="w-full bg-transparent border-b border-slate-700 hover:border-slate-500 focus:border-cyan-500 focus:outline-none text-slate-300 text-xs py-0.5 px-0 transition-colors"
              placeholder="Enter assembly name..."
              data-testid="assembly-name-input"
            />
          ) : (isDraft && line.make_buy === 'Make') ? (
            <>
              <input
                type="text"
                value={editValues.part_name || ''}
                onChange={e => handleFieldChange('part_name', e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent border-b border-slate-700 hover:border-slate-500 focus:border-cyan-500 focus:outline-none text-slate-300 text-xs py-0.5 px-0 transition-colors"
                placeholder="Part name..."
                data-testid="make-part-name-input"
              />
              <input
                type="text"
                value={editValues.part_description || ''}
                onChange={e => handleFieldChange('part_description', e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent border-b border-slate-700/30 hover:border-slate-500 focus:border-cyan-500 focus:outline-none text-slate-500 text-[11px] py-0.5 px-0 transition-colors mt-0.5"
                placeholder="Description (optional)..."
                data-testid="make-part-description-input"
              />
            </>
          ) : (
            <>
              <div className="text-slate-300 text-xs">{line.part_name || line.part_description}</div>
              {line.part_description && line.part_name && line.part_description !== line.part_name && (
                <div className="text-slate-500 text-[11px] mt-0.5">{line.part_description}</div>
              )}
            </>
          )}
        </div>
        <div className="text-[10px] text-slate-600 mt-0.5" data-testid="detail-level-info">
          Level {line.level_depth} │ Find #{line.find_number}
          {(() => {
            if (!line.parent_line_id) return ' │ NHA: Root';
            const parent = allLines.find(l => l.line_id === line.parent_line_id);
            if (!parent) return null;
            const parentName = parent.is_assembly
              ? (parent.assembly_name || parent.assembly_number || parent.part_id)
              : (parent.part_number || parent.part_id);
            return <> │ <span className="text-cyan-600">NHA: {parentName}</span></>;
          })()}
        </div>
      </div>

      {/* Basic Fields */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500 mb-1 block">Find #</Label>
          <div className="font-mono text-slate-300 text-sm bg-slate-800/50 px-2 py-1 rounded border border-slate-700/30">{line.find_number}</div>
        </div>
        <div>
          <Label className="text-[10px] text-slate-500 mb-1 block">Quantity *</Label>
          <Input type="number" value={editValues.quantity || ''} onChange={e => handleFieldChange('quantity', e.target.value)}
            onBlur={handleBlur} onKeyDown={handleKeyDown}
            className="h-7 text-xs bg-slate-800 border-slate-600 text-white font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            min="0.001" step="any" data-testid="detail-quantity-input" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500 mb-1 block">UOM</Label>
          <div className="text-slate-300 text-sm bg-slate-800/50 px-2 py-1 rounded border border-slate-700/30">{line.uom}</div>
        </div>
      </div>

      {!line.is_assembly && (
        <div>
          <Label className="text-[10px] text-slate-500 mb-1 block">Reference Designator</Label>
          <Input type="text" value={editValues.reference_designator || ''} onChange={e => handleFieldChange('reference_designator', e.target.value)}
            onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder="e.g., R1, R2, R3"
            className="h-7 text-xs bg-slate-800 border-slate-600 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
        </div>
      )}

      {/* Classification */}
      <div>
        <Label className="text-[10px] text-slate-500">Classification <span className="text-amber-600/60">[8.5.1]</span></Label>
        <div className="mt-1"><ClassBadge type={line.make_buy} isAssembly={line.is_assembly} /></div>
        {line.is_assembly && (
          <div className="mt-1.5 text-[10px] text-slate-500 bg-blue-900/10 border border-blue-800/30 rounded p-2">
            ℹ Assembly: Contains nested parts. Select this node and add parts from search to nest them here.
          </div>
        )}
        {isMakeNonAssembly && (
          <div className="mt-1.5 text-[10px] text-slate-500 bg-blue-900/10 border border-blue-800/30 rounded p-2">
            ℹ Make: Fabricated in-house. Set material profile and input parameters — system auto-derives costs.
          </div>
        )}
      </div>

      {/* Inline Cost / Weight / Lead Time editing — Buy, RM, Fastener parts */}
      {!line.is_assembly && line.make_buy !== 'Make' && isDraft && (
        <div className="border border-emerald-700/30 rounded-lg p-3 bg-emerald-900/10" data-testid="buy-part-cost-section">
          <div className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider mb-2">
            Procurement Details
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-slate-400 mb-1 block">Unit Cost (₹)</Label>
              <Input type="number" value={editValues.unit_cost || ''} onChange={e => handleFieldChange('unit_cost', e.target.value)}
                onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder="0.00"
                className="h-7 text-xs bg-slate-800 border-slate-600 text-white font-mono focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                min="0" step="0.01" data-testid="detail-unit-cost-input" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-400 mb-1 block">Weight (kg)</Label>
              <Input type="number" value={editValues.unit_weight_kg || ''} onChange={e => handleFieldChange('unit_weight_kg', e.target.value)}
                onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder="0.000"
                className="h-7 text-xs bg-slate-800 border-slate-600 text-white font-mono focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                min="0" step="0.001" data-testid="detail-unit-weight-input" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-400 mb-1 block">Lead Time (days)</Label>
              <Input type="number" value={editValues.lead_time_days || ''} onChange={e => handleFieldChange('lead_time_days', e.target.value)}
                onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder="0"
                className="h-7 text-xs bg-slate-800 border-slate-600 text-white font-mono focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                min="0" step="1" data-testid="detail-lead-time-input" />
            </div>
          </div>
          {(line.unit_cost > 0 || line.unit_weight_kg > 0) && (
            <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-3">
              {line.unit_cost > 0 && <span>Extended: <span className="text-emerald-400 font-mono font-semibold">₹{((line.quantity || 1) * line.unit_cost).toLocaleString()}</span></span>}
              {line.unit_weight_kg > 0 && <span>Total Wt: <span className="text-blue-400 font-mono">{((line.quantity || 1) * line.unit_weight_kg).toFixed(3)} kg</span></span>}
              {line.lead_time_days > 0 && <span>Lead: <span className="text-amber-400 font-mono">{line.lead_time_days}d</span></span>}
            </div>
          )}
          <div className="mt-1.5 text-[9px] text-slate-600">
            Changes sync to Parts Catalog & source library
          </div>
        </div>
      )}
      {/* Read-only cost summary for non-draft Buy parts */}
      {!line.is_assembly && line.make_buy !== 'Make' && !isDraft && (line.unit_cost > 0 || line.unit_weight_kg > 0) && (
        <div className="border border-slate-700/30 rounded-lg p-3 bg-slate-800/20" data-testid="buy-part-cost-readonly">
          <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-2">Procurement Details</div>
          <div className="space-y-1 text-[10px]">
            {line.unit_cost > 0 && <div className="flex justify-between"><span className="text-slate-400">Unit Cost:</span><span className="font-mono text-emerald-400">₹{line.unit_cost.toLocaleString()}</span></div>}
            {line.unit_weight_kg > 0 && <div className="flex justify-between"><span className="text-slate-400">Unit Weight:</span><span className="font-mono text-blue-400">{line.unit_weight_kg} kg</span></div>}
            {line.lead_time_days > 0 && <div className="flex justify-between"><span className="text-slate-400">Lead Time:</span><span className="font-mono text-amber-400">{line.lead_time_days} days</span></div>}
          </div>
        </div>
      )}

      {/* Engineering Parameters (Make parts, non-assembly) */}
      {isMakeNonAssembly && (
        <div className="border border-blue-700/30 rounded-lg p-3 bg-blue-900/10">
          <div className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider mb-3">
            Engineering Parameters <span className="text-amber-600/60">[8.3.5]</span>
          </div>
          <div className="mb-3">
            <Label className="text-[10px] text-slate-400 mb-1 block">Raw Material Source</Label>
            <RMLibrarySelector
              value={editValues.material_profile_id || ''}
              onChange={(code, adapted) => {
                setRmAdaptedProfile(adapted);
                // Only trigger field change if the code actually changed (avoid re-save on initial resolve)
                if (code !== (editValues.material_profile_id || '')) {
                  handleFieldChange('material_profile_id', code);
                }
              }}
              disabled={!isDraft}
            />
            {selectedProfile && (
              <div className="mt-1.5 text-[10px] text-slate-500 grid grid-cols-2 gap-x-2">
                {isLinearProfile ? (
                  <>
                    {selectedProfile.linear_mass_kg_per_m && <span>Linear Mass: {selectedProfile.linear_mass_kg_per_m} kg/m</span>}
                    {selectedProfile.stock_length_mm && <span>Stock: {selectedProfile.stock_length_mm}mm</span>}
                    {selectedProfile.stock_od_mm && <span>OD: {selectedProfile.stock_od_mm}mm</span>}
                    {selectedProfile.stock_wall_mm && <span>Wall: {selectedProfile.stock_wall_mm}mm</span>}
                    {selectedProfile.cost_per_stock_unit > 0 && <span>Cost: ₹{selectedProfile.cost_per_stock_unit}/length</span>}
                    <span>Form: {selectedProfile.material_form?.replace('_', ' ')}</span>
                  </>
                ) : (
                  <>
                    <span>Density: {selectedProfile.density_kg_m3} kg/m³</span>
                    <span>Thickness: {selectedProfile.thickness_mm}mm</span>
                    <span>Sheet: {selectedProfile.stock_length_mm}x{selectedProfile.stock_width_mm}mm</span>
                    <span>Buffer: {selectedProfile.nesting_buffer_pct}%</span>
                  </>
                )}
              </div>
            )}
          </div>
          {/* Dynamic input: Length for linear stock, Surface Area for sheet/plate */}
          {isLinearProfile ? (
            <div className="bg-slate-900/50 rounded-lg p-2.5 border border-blue-600/30">
              <Label className="text-[10px] text-blue-300 mb-1 block font-semibold">Cut Length (mm) &larr; YOU TYPE THIS</Label>
              <Input type="number" value={editValues.length_m ? (editValues.length_m * 1000) : ''} onChange={e => {
                const mm = parseFloat(e.target.value);
                handleFieldChange('length_m', isNaN(mm) ? '' : mm / 1000);
              }}
                onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder="e.g., 300"
                className="h-8 text-sm bg-slate-800 border-blue-600/50 text-white font-mono font-semibold focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                min="0" step="1" data-testid="detail-length-input" />
              {editValues.length_m > 0 && <div className="text-[9px] text-slate-500 mt-1">= {editValues.length_m}m</div>}
              {/* Inline Material Cost Preview — computes in real-time from profile + cut_length */}
              {editValues.length_m > 0 && selectedProfile && (() => {
                const lm = parseFloat(editValues.length_m) || 0;
                const linearMass = selectedProfile.linear_mass_kg_per_m || 0;
                const stockLen = selectedProfile.stock_length_mm || 6000;
                const kerf = selectedProfile.kerf_loss_mm || 0;
                const costPerUnit = selectedProfile.cost_per_stock_unit || 0;
                const weight = linearMass > 0 ? Math.round(lm * linearMass * 10000) / 10000 : 0;
                const stockFraction = stockLen > 0 ? Math.round(((lm * 1000) + kerf) / stockLen * 1000000) / 1000000 : 0;
                const materialCost = costPerUnit > 0 ? Math.round(stockFraction * costPerUnit * 100) / 100 : 0;
                return (
                  <div className="mt-2 space-y-0.5 text-[10px] border-t border-blue-600/20 pt-2" data-testid="inline-material-cost">
                    {weight > 0 && <div className="flex justify-between"><span className="text-slate-400">Weight:</span><span className="font-mono text-white font-semibold">{weight} kg</span></div>}
                    {stockFraction > 0 && <div className="flex justify-between"><span className="text-slate-400">Stock Fraction:</span><span className="font-mono text-slate-300">{stockFraction}</span></div>}
                    <div className="flex justify-between">
                      <span className="text-emerald-400/80 font-semibold">Material Cost:</span>
                      {costPerUnit > 0
                        ? <span className="font-mono text-emerald-400 font-bold">₹{materialCost.toLocaleString('en-IN')}</span>
                        : <span className="text-amber-400/70 italic text-[9px]">No price — update in RM Library</span>
                      }
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="bg-slate-900/50 rounded-lg p-2.5 border border-blue-600/30">
              <Label className="text-[10px] text-blue-300 mb-1 block font-semibold">Surface Area (m2) &larr; YOU TYPE THIS</Label>
              <Input type="number" value={editValues.surface_area_m2 || ''} onChange={e => handleFieldChange('surface_area_m2', e.target.value)}
                onBlur={handleBlur} onKeyDown={handleKeyDown} placeholder="e.g., 0.24"
                className="h-8 text-sm bg-slate-800 border-blue-600/50 text-white font-mono font-semibold focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
                min="0" step="0.001" data-testid="detail-surface-area-input" />
              {/* Inline Material Cost Preview for sheet/plate */}
              {parseFloat(editValues.surface_area_m2) > 0 && selectedProfile && (() => {
                const sa = parseFloat(editValues.surface_area_m2) || 0;
                const thickness = selectedProfile.thickness_mm || 0;
                const density = selectedProfile.density_kg_m3 || 0;
                const usableArea = selectedProfile.stock_usable_area_m2 || 0;
                const bufferPct = selectedProfile.nesting_buffer_pct || 30;
                const costPerUnit = selectedProfile.cost_per_stock_unit || 0;
                const weight = thickness > 0 && density > 0 ? Math.round(sa * (thickness / 1000) * density * 1000) / 1000 : 0;
                const sheetsReq = usableArea > 0 ? Math.round(sa / usableArea * (1 + bufferPct / 100) * 10000) / 10000 : 0;
                const materialCost = costPerUnit > 0 && sheetsReq > 0 ? Math.round(sheetsReq * costPerUnit * 100) / 100 : 0;
                return (
                  <div className="mt-2 space-y-0.5 text-[10px] border-t border-blue-600/20 pt-2" data-testid="inline-material-cost">
                    {weight > 0 && <div className="flex justify-between"><span className="text-slate-400">Weight:</span><span className="font-mono text-white font-semibold">{weight} kg</span></div>}
                    {sheetsReq > 0 && <div className="flex justify-between"><span className="text-slate-400">Sheets Required:</span><span className="font-mono text-slate-300">{sheetsReq}</span></div>}
                    <div className="flex justify-between">
                      <span className="text-emerald-400/80 font-semibold">Material Cost:</span>
                      {costPerUnit > 0
                        ? <span className="font-mono text-emerald-400 font-bold">₹{materialCost.toLocaleString('en-IN')}</span>
                        : <span className="text-amber-400/70 italic text-[9px]">No price — update in RM Library</span>
                      }
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* What-If Preview */}
      {whatIfMode && whatIfPreview && (
        <div className="border-2 border-amber-500/50 rounded-lg p-3 bg-amber-900/20 animate-in fade-in duration-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider flex items-center gap-1">
              <Calculator className="w-3 h-3" /> What-If Preview
            </div>
            {loadingPreview && <span className="text-[10px] text-amber-300 animate-pulse">Computing...</span>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-[11px]">
            <div className="bg-slate-900/50 rounded p-2">
              <div className="text-slate-500 font-semibold mb-1 text-[10px]">CURRENT</div>
              <div className="flex justify-between"><span className="text-slate-400">Material:</span><span className="font-mono text-slate-300">₹{(whatIfPreview.current?.material_cost || whatIfPreview.current?.sheet_cost || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Treatment:</span><span className="font-mono text-slate-300">₹{(whatIfPreview.current?.total_process_cost || 0).toLocaleString()}</span></div>
              {whatIfPreview.current?.weight_kg > 0 && (
                <div className="flex justify-between"><span className="text-slate-400">Weight:</span><span className="font-mono text-slate-300">{whatIfPreview.current.weight_kg} kg</span></div>
              )}
              <div className="border-t border-slate-700 mt-1 pt-1 flex justify-between font-semibold">
                <span className="text-slate-300">Total:</span><span className="font-mono text-white">₹{whatIfPreview.summary.current_total.toLocaleString()}</span>
              </div>
            </div>
            <div className="bg-amber-900/30 rounded p-2 border border-amber-600/30">
              <div className="text-amber-400 font-semibold mb-1 text-[10px]">PROPOSED</div>
              <div className="flex justify-between"><span className="text-amber-300/70">Material:</span><span className="font-mono text-amber-200">₹{(whatIfPreview.proposed?.material_cost || whatIfPreview.proposed?.sheet_cost || 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-amber-300/70">Treatment:</span><span className="font-mono text-amber-200">₹{(whatIfPreview.proposed?.total_process_cost || 0).toLocaleString()}</span></div>
              {whatIfPreview.proposed?.weight_kg > 0 && (
                <div className="flex justify-between"><span className="text-amber-300/70">Weight:</span><span className="font-mono text-amber-200">{whatIfPreview.proposed.weight_kg} kg</span></div>
              )}
              <div className="border-t border-amber-600/30 mt-1 pt-1 flex justify-between font-semibold">
                <span className="text-amber-300">Total:</span><span className="font-mono text-amber-100">₹{whatIfPreview.summary.proposed_total.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className={`mt-2 rounded p-2 flex items-center justify-between ${
            whatIfPreview.delta.is_increase ? 'bg-red-900/30 border border-red-600/30' :
            whatIfPreview.delta.is_decrease ? 'bg-green-900/30 border border-green-600/30' : 'bg-slate-800/50'
          }`}>
            <span className="text-[10px] font-semibold">{whatIfPreview.delta.is_increase ? '📈 Increase' : whatIfPreview.delta.is_decrease ? '📉 Decrease' : 'No Change'}</span>
            <span className={`font-mono font-bold text-sm ${whatIfPreview.delta.is_increase ? 'text-red-400' : whatIfPreview.delta.is_decrease ? 'text-green-400' : 'text-slate-400'}`}>
              {whatIfPreview.delta.total_cost > 0 ? '+' : ''}₹{whatIfPreview.delta.total_cost.toLocaleString()} ({whatIfPreview.delta.percentage_change})
            </span>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="flex-1 h-7 text-[10px] text-white bg-amber-600 hover:bg-amber-500"
              onClick={handleApplyChanges} disabled={saving}>
              {saving ? 'Applying...' : '✓ Apply Changes'}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px] border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={handleCancelChanges}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Fabrication Operations (Make parts, non-assembly) */}
      {isMakeNonAssembly && (
        <FabricationOpsSection line={line} bomId={bomId} revisionId={revisionId} onRefresh={onRefresh} isDraft={isDraft} onBusy={onBusy} />
      )}

      {/* Surface Preparation & Treatment (Make parts, non-assembly) */}
      {isMakeNonAssembly && (
        <SurfaceTreatmentSection line={line} bomId={bomId} revisionId={revisionId} onRefresh={onRefresh} isDraft={isDraft} onBusy={onBusy} />
      )}

      {/* Manufacturing Template Actions (Save / Apply) */}
      {isMakeNonAssembly && (
        <ManufacturingTemplateActions line={line} bomId={bomId} revisionId={revisionId}
          onRefresh={onRefresh} isDraft={isDraft} materialProfile={selectedProfile} onBusy={onBusy} />
      )}


      {/* Auto-Derived Values + 3-Layer Cost Breakdown */}
      {derived && !whatIfMode && (
        <div className="border border-slate-700/30 rounded-lg p-3 bg-slate-800/20" data-testid="derived-values-section">
          <div className="text-[10px] text-cyan-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Cost Breakdown <span className="text-amber-600/60">[8.5.1]</span>
          </div>

          {/* Layer 1: Raw Material */}
          <div className="text-[10px] text-slate-500 mb-1 font-semibold">RAW MATERIAL:</div>
          {derived.weight_kg != null && <div className="flex justify-between py-0.5"><span className="text-slate-400">Weight:</span><span className="font-mono text-white">{derived.weight_kg} kg</span></div>}
          {derived.sheets_required != null && <div className="flex justify-between py-0.5"><span className="text-slate-400">Sheets:</span><span className="font-mono text-white">{derived.sheets_required}</span></div>}
          {derived.sheet_cost != null && <div className="flex justify-between py-0.5"><span className="text-slate-400">Sheet Cost:</span><span className="font-mono text-emerald-400 font-semibold">{(liveMaterialCost ?? derived.sheet_cost).toLocaleString()}</span></div>}
          {derived.stock_fraction != null && <div className="flex justify-between py-0.5"><span className="text-slate-400">Stock Fraction:</span><span className="font-mono text-white">{derived.stock_fraction}</span></div>}
          {derived.outer_surface_area_m2 != null && <div className="flex justify-between py-0.5"><span className="text-slate-400">Outer SA:</span><span className="font-mono text-white">{derived.outer_surface_area_m2} m2</span></div>}
          {derived.material_cost != null && !derived.sheet_cost && <div className="flex justify-between py-0.5"><span className="text-slate-400">Material Cost:</span><span className="font-mono text-emerald-400 font-semibold">{(liveMaterialCost ?? derived.material_cost).toLocaleString()}</span></div>}

          {/* Layer 2: Fabrication Summary */}
          {(line.fabrication_cost_total > 0 || (line.fabrication_operations?.length > 0)) && (
            <>
              <div className="border-t border-slate-700/50 my-2" />
              <div className="text-[10px] text-slate-500 mb-1 font-semibold">FABRICATION:</div>
              {(line.fabrication_operations || []).map((op, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span className="text-slate-400 text-[10px]"><span className="text-orange-400/60">{op.sequence_order}.</span> {op.operation_name}</span>
                  <span className="font-mono text-white text-[10px]">{op.operation_cost?.toLocaleString()}</span>
                </div>
              ))}
            </>
          )}

          {/* Layer 3: Treatment Summary — ONLY from explicitly applied rules */}
          {(() => {
            const appliedRules = line.applied_process_rules || [];
            const selectedRules = appliedRules.filter(r => r.is_selected);
            if (selectedRules.length === 0) return null;
            return (
              <>
                <div className="border-t border-slate-700/50 my-2" />
                <div className="text-[10px] text-slate-500 mb-1 font-semibold">TREATMENT:</div>
                {selectedRules.map((item, i) => {
                  const name = item.rule_name || item.name;
                  return (
                    <div key={i} className="flex justify-between py-0.5">
                      <span className="text-slate-400 text-[10px]">{name}</span>
                      <span className="font-mono text-white text-[10px]">{item.cost?.toLocaleString()}</span>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {/* TOTAL — 3-Layer Breakdown */}
          {(() => {
            const storedMatCost = derived.material_cost || derived.sheet_cost || 0;
            const matCost = liveMaterialCost ?? storedMatCost;
            const fabCost = line.fabrication_cost_total || 0;
            const appliedRules = line.applied_process_rules || [];
            const treatCost = appliedRules.reduce((s, r) => s + (r.is_selected ? (r.cost || 0) : 0), 0);
            const unitCost = matCost + fabCost + treatCost;
            const qty = line.quantity || 1;
            return (
              <div className="border-t border-cyan-500/30 mt-2 pt-2" data-testid="cost-breakdown-total">
                <div className="space-y-0.5 text-[10px] mb-1.5">
                  <div className="flex justify-between"><span className="text-emerald-400/70">Raw Material:</span><span className="font-mono text-emerald-400/70">{matCost.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-orange-400/70">Fabrication:</span><span className="font-mono text-orange-400/70">{fabCost.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-violet-400/70">Treatment:</span><span className="font-mono text-violet-400/70">{treatCost.toLocaleString()}</span></div>
                </div>
                <div className="flex justify-between">
                  <span className="text-white font-semibold text-[11px]">UNIT COST:</span>
                  <span className="font-mono text-cyan-400 font-bold text-[11px]" data-testid="unit-make-cost">{unitCost.toLocaleString()}</span>
                </div>
                {qty > 1 && (
                  <div className="flex justify-between mt-1 pt-1 border-t border-slate-700/30">
                    <span className="text-white font-semibold text-[11px]">EXTENDED ({qty} pcs):</span>
                    <span className="font-mono text-cyan-300 font-bold text-[11px]" data-testid="extended-make-cost">{(qty * unitCost).toLocaleString()}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Manual Preview Cost Impact button */}
          {editValues.material_profile_id && (parseFloat(editValues.length_m) > 0 || parseFloat(editValues.surface_area_m2) > 0) && (
            <button
              data-testid="manual-preview-cost-btn"
              onClick={handleManualPreview}
              disabled={loadingPreview}
              className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded border border-amber-500/40 bg-amber-900/15 text-amber-400 text-[10px] font-semibold hover:bg-amber-900/30 hover:border-amber-500/60 transition-colors disabled:opacity-50"
            >
              <Calculator className="w-3 h-3" />
              {loadingPreview ? 'Computing...' : 'Preview Cost Impact'}
            </button>
          )}
        </div>
      )}

      {/* Cost Summary */}
      {(() => {
        const mb = line.make_buy || 'Buy';
        const isAsm = line.is_assembly;
        const derived = line.derived;
        let unitCost = 0;
        if (isAsm) {
          unitCost = line.unit_cost || 0;
        } else if (mb === 'Make') {
          const storedMat = derived ? (derived.material_cost || derived.sheet_cost || 0) : 0;
          const matCost = liveMaterialCost ?? storedMat;
          const fabCost = line.fabrication_cost_total || 0;
          const rules = line.applied_process_rules || [];
          const treatCost = rules.reduce((s, r) => s + (r.is_selected ? (r.cost || 0) : 0), 0);
          unitCost = matCost + fabCost + treatCost;
        } else {
          unitCost = line.unit_cost || 0;
        }
        if (unitCost <= 0 && !isAsm) return null;
        const qty = line.quantity || 1;
        const extCost = qty * unitCost;

        // For assemblies, get children breakdown
        const childLines = isAsm ? allLines.filter(l => l.parent_line_id === line.line_id) : [];

        return (
          <div className="border border-emerald-700/20 rounded-lg p-3 bg-emerald-900/10" data-testid="cost-summary-section">
            <div className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider mb-2">Cost</div>

            {/* Assembly child breakdown */}
            {isAsm && childLines.length > 0 && (
              <div className="space-y-0.5 mb-2 pb-2 border-b border-slate-700/30">
                {childLines.map(child => {
                  const childQty = child.quantity || 1;
                  let childUnitCost = 0;
                  if (child.is_assembly) {
                    childUnitCost = child.unit_cost || 0;
                  } else if (child.make_buy === 'Make') {
                    const cd = child.derived;
                    childUnitCost = (cd ? (cd.material_cost || cd.sheet_cost || 0) : 0)
                      + (child.fabrication_cost_total || 0)
                      + ((child.applied_process_rules || []).reduce((s, r) => s + (r.is_selected ? (r.cost || 0) : 0), 0)
                        || (cd?.total_process_cost || 0));
                  } else {
                    childUnitCost = child.unit_cost || 0;
                  }
                  const childExt = childQty * childUnitCost;
                  if (childUnitCost <= 0 && !child.is_assembly) return null;
                  return (
                    <div key={child.line_id} className="flex items-center justify-between text-[10px]">
                      <span className="text-slate-400 truncate max-w-[200px]" title={child.part_name || child.part_description}>
                        {child.part_number || child.assembly_number || '?'}{' '}
                        <span className="text-slate-600">{(child.part_name || child.part_description)?.substring(0, 25)}{(child.part_name || child.part_description)?.length > 25 ? '...' : ''}</span>
                        {childQty > 1 && <span className="text-slate-500 ml-1">x{childQty}</span>}
                      </span>
                      <span className="font-mono text-emerald-400/70 shrink-0 ml-2">
                        {childExt > 0 ? `₹${childExt.toLocaleString()}` : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-between py-0.5">
              <span className="text-slate-400 text-[10px]">Unit Cost:</span>
              <span className="font-mono text-emerald-300 text-[10px]">₹{unitCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            {qty > 1 && (
              <div className="flex justify-between py-0.5 border-t border-slate-700/30 mt-1 pt-1">
                <span className="text-white font-semibold text-[11px]">Extended ({qty} pcs):</span>
                <span className="font-mono text-emerald-300 font-bold text-[11px]">₹{extCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            )}
            {line.make_buy === 'Make' && !isAsm && (line.derived?.material_cost > 0 || line.fabrication_cost_total > 0) && (
              <div className="border-t border-slate-700/30 mt-1.5 pt-1.5 space-y-0.5 text-[10px]">
                <div className="flex justify-between"><span className="text-slate-500">Material:</span><span className="font-mono text-slate-400">₹{(line.derived?.material_cost || line.derived?.sheet_cost || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Fabrication:</span><span className="font-mono text-slate-400">₹{(line.fabrication_cost_total || 0).toLocaleString()}</span></div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Weight Summary */}
      {(() => {
        const unitWt = line.derived?.weight_kg || line.unit_weight_kg || 0;
        if (unitWt <= 0) return null;
        const qty = line.quantity || 1;
        const extWt = qty * unitWt;
        const isAsm = line.is_assembly;
        const childLines = isAsm ? allLines.filter(l => l.parent_line_id === line.line_id) : [];

        return (
          <div className="border border-blue-700/20 rounded-lg p-3 bg-blue-900/10" data-testid="weight-summary-section">
            <div className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider mb-2">Weight</div>

            {/* Assembly child weight breakdown */}
            {isAsm && childLines.length > 0 && (
              <div className="space-y-0.5 mb-2 pb-2 border-b border-slate-700/30">
                {childLines.map(child => {
                  const childQty = child.quantity || 1;
                  const childWt = child.derived?.weight_kg || child.unit_weight_kg || 0;
                  const childExtWt = childQty * childWt;
                  if (childWt <= 0) return null;
                  return (
                    <div key={child.line_id} className="flex items-center justify-between text-[10px]">
                      <span className="text-slate-400 truncate max-w-[200px]" title={child.part_name || child.part_description}>
                        {child.part_number || child.assembly_number || '?'}{' '}
                        <span className="text-slate-600">{(child.part_name || child.part_description)?.substring(0, 25)}{(child.part_name || child.part_description)?.length > 25 ? '...' : ''}</span>
                        {childQty > 1 && <span className="text-slate-500 ml-1">x{childQty}</span>}
                      </span>
                      <span className="font-mono text-blue-400/70 shrink-0 ml-2">{childExtWt.toFixed(3)} kg</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-between py-0.5">
              <span className="text-slate-400 text-[10px]">Unit Weight:</span>
              <span className="font-mono text-blue-300 text-[10px]">{unitWt.toFixed(3)} kg</span>
            </div>
            {qty > 1 && (
              <div className="flex justify-between py-0.5 border-t border-slate-700/30 mt-1 pt-1">
                <span className="text-white font-semibold text-[11px]">Extended ({qty} pcs):</span>
                <span className="font-mono text-blue-300 font-bold text-[11px]">{extWt.toFixed(3)} kg</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Actions */}
      <div className="pt-2 border-t border-slate-700/50">
        <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-900/20 text-[10px] h-7" onClick={() => onRemove(line.line_id)}
          data-testid="detail-remove-line-btn">
          <Trash2 className="w-3 h-3 mr-1" /> Remove Line
        </Button>
      </div>
    </div>
  );
};

/* ================================================================== */
/* BOM EDITOR PAGE                                                     */
/* ================================================================== */
const BomEditorPageInner = () => {
  const { bomId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useBomTheme();
  const { user: authUser } = useAuth();
  const [bom, setBom] = useState(null);
  const [revision, setRevision] = useState(null);
  const [lines, setLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [addClassification, setAddClassification] = useState('Buy');
  const [activeLibraryTab, setActiveLibraryTab] = useState('product_builder');
  const searchTimerRef = useRef(null);

  // Enhancement 2: Centralized expand/collapse state
  const [expandedMap, setExpandedMap] = useState({});
  // Enhancement 3: Insertion context — which parent node new parts go under
  const [insertionParentId, setInsertionParentId] = useState(null);
  // Enhancement 3: Create Assembly form
  const [showCreateAssembly, setShowCreateAssembly] = useState(false);
  const [newAssemblyName, setNewAssemblyName] = useState('');
  // Enhancement 3b: Create Make Part form
  const [showCreateMakePart, setShowCreateMakePart] = useState(false);
  const [newMakePartName, setNewMakePartName] = useState('');
  const [newMakePartDesc, setNewMakePartDesc] = useState('');
  const [newMakePartProfile, setNewMakePartProfile] = useState('');
  const [makePartTypes, setMakePartTypes] = useState([]);
  const [selectedPartType, setSelectedPartType] = useState(null);
  const [newMakePartPurpose, setNewMakePartPurpose] = useState('');
  const [showCreatePartType, setShowCreatePartType] = useState(false);
  const [newPartTypeName, setNewPartTypeName] = useState('');
  const [newPartTypeDesc, setNewPartTypeDesc] = useState('');
  const [makePartTypeSearch, setMakePartTypeSearch] = useState('');
  // Fastener selector
  const [showFastenerSelector, setShowFastenerSelector] = useState(false);
  const [detailTab, setDetailTab] = useState('detail'); // 'detail' | 'activity'
  // Enhancement 2: Active level for level selector
  const [activeLevel, setActiveLevel] = useState(null);
  // Enhancement 4: Tree view mode (compact vs comfortable)
  const [treeViewMode, setTreeViewMode] = useState('compact');
  // Enhancement 2: Sub-assembly preview
  const [previewAssembly, setPreviewAssembly] = useState(null);
  const [previewLines, setPreviewLines] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Approval workflow state
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [processingApproval, setProcessingApproval] = useState(false);
  const [approvalPermissions, setApprovalPermissions] = useState(null);

  // Revision management state
  const [revisionList, setRevisionList] = useState([]);
  const [showRevisionPanel, setShowRevisionPanel] = useState(false);
  const [showNewRevisionDialog, setShowNewRevisionDialog] = useState(false);
  const [newRevisionReason, setNewRevisionReason] = useState('');
  const [creatingRevision, setCreatingRevision] = useState(false);

  // Phase 6.1: BOM action busy state — immediate visual feedback
  const [bomBusy, setBomBusy] = useState(null); // null | 'adding' | 'creating' | 'removing' | 'updating' | 'loading'
  const withBusy = useCallback((label, fn) => async (...args) => {
    setBomBusy(label);
    try { return await fn(...args); }
    finally { setBomBusy(null); }
  }, []);

  const fetchBom = useCallback(async () => {
    const tk = getToken();
    if (!tk || !bomId) return;
    setBomBusy(prev => prev || 'loading');
    try {
      const [bomRes, profRes, typesRes] = await Promise.all([
        fetchWithAuth(`${API}/api/v1/bom/${bomId}`, { headers: { Authorization: `Bearer ${tk}` } }),
        fetchWithAuth(`${API}/api/v1/bom/material-profiles`, { headers: { Authorization: `Bearer ${tk}` } }),
        fetchWithAuth(`${API}/api/v1/bom/make-part-types`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      if (bomRes.ok) {
        const b = await bomRes.json();
        setBom(b);
        if (b.current_revision_id) {
          const revRes = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${b.current_revision_id}`, { headers: { Authorization: `Bearer ${tk}` } });
          if (revRes.ok) {
            const r = await revRes.json();
            setRevision(r);
            setLines(r.lines || []);
          }
        }
      }
      if (profRes.ok) setProfiles(await profRes.json());
      if (typesRes.ok) setMakePartTypes(await typesRes.json());
    } catch (e) { console.error(e); }
    finally { setBomBusy(null); }
  }, [bomId]);

  useEffect(() => { fetchBom(); }, [fetchBom]);

  // Fetch revision list for revision selector
  const fetchRevisions = useCallback(async () => {
    const tk = getToken();
    if (!tk || !bomId) return;
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) setRevisionList(await res.json());
    } catch (e) { console.error(e); }
  }, [bomId]);

  useEffect(() => { fetchRevisions(); }, [fetchRevisions]);

  // Switch to a different revision
  const switchRevision = useCallback(async (revisionId) => {
    const tk = getToken();
    if (!tk || !bomId) return;
    setBomBusy('loading');
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revisionId}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) {
        const r = await res.json();
        setRevision(r);
        setLines(r.lines || []);
        setSelectedLine(null);
        setShowRevisionPanel(false);
      }
    } catch (e) { console.error(e); }
    finally { setBomBusy(null); }
  }, [bomId]);

  // Create new revision (copy-on-write)
  const createNewRevision = useCallback(async () => {
    const tk = getToken();
    if (!tk || !bomId || !revision) return;
    setCreatingRevision(true);
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tk}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_revision_id: revision.revision_id,
          change_reason: newRevisionReason || 'New revision',
        }),
      });
      if (res.ok) {
        const newRev = await res.json();
        setRevision(newRev);
        setLines(newRev.lines || []);
        setSelectedLine(null);
        setShowNewRevisionDialog(false);
        setNewRevisionReason('');
        await fetchRevisions();
        await fetchBom();
        toast.success(`Revision ${newRev.revision_number} created (${newRev.lines?.length || 0} lines copied)`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create revision');
      }
    } catch (e) { toast.error('Failed to create revision'); console.error(e); }
    finally { setCreatingRevision(false); }
  }, [bomId, revision, newRevisionReason, fetchRevisions, fetchBom]);

  // Fetch approval permissions
  useEffect(() => {
    (async () => {
      const tk = getToken();
      if (!tk) return;
      try {
        const res = await fetchWithAuth(`${API}/api/v1/bom/approvals/permissions`, { headers: { Authorization: `Bearer ${tk}` } });
        if (res.ok) setApprovalPermissions(await res.json());
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      const tk = getToken();
      try {
        const res = await fetchWithAuth(`${API}/api/v1/bom/parts/search?q=${encodeURIComponent(searchQuery)}&limit=20`, { headers: { Authorization: `Bearer ${tk}` } });
        if (res.ok) setSearchResults(await res.json());
      } catch (e) { console.error(e); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  // Enhancement 3: Update insertion context when selecting a tree node
  const handleSelectLine = useCallback((lineId) => {
    setSelectedLine(lineId);
    // When single-selecting, clear multi-select
    setSelectedLineIds(new Set());
    if (!lineId) { setInsertionParentId(null); return; }
    const line = lines.find(l => l.line_id === lineId);
    if (line?.is_assembly) {
      setInsertionParentId(lineId);
    }
  }, [lines]);

  // Multi-select: toggle a line's checkbox
  const handleToggleLineSelect = useCallback((lineId, e) => {
    if (e) e.stopPropagation();
    setSelectedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, []);

  const handleClearMultiSelect = useCallback(() => {
    setSelectedLineIds(new Set());
  }, []);

  const isMultiSelectActive = selectedLineIds.size > 0;

  // Enhancement 3: Add part with parent context
  const addPart = async (part) => {
    const tk = getToken();
    if (!revision || bomBusy) return;
    setBomBusy('adding');
    try {
      const body = {
        part_id: part.hpn_compact,
        quantity: 1,
        make_buy: addClassification,
        parent_line_id: insertionParentId || undefined,
      };
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const line = await res.json();
        toast.success(`${part.hpn_compact} added as ${addClassification}${insertionParentId ? ' (nested)' : ''}`);
        await fetchBom();
        setSelectedLine(line.line_id);
        // Ensure parent is expanded
        if (insertionParentId) setExpandedMap(prev => ({ ...prev, [insertionParentId]: true }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBomBusy(null); }
  };

  // Enhancement 3: Create sub-assembly
  const createAssembly = async () => {
    if (!newAssemblyName.trim()) { toast.error('Assembly name is required'); return; }
    const tk = getToken();
    if (!revision || bomBusy) return;
    setBomBusy('creating');
    try {
      const body = {
        part_id: `ASM-TMP-${Date.now()}`,
        assembly_name: newAssemblyName.trim(),
        quantity: 1,
        make_buy: 'Make',
        is_assembly: true,
        parent_line_id: insertionParentId || undefined,
      };
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const line = await res.json();
        toast.success(`Assembly "${newAssemblyName}" created`);
        setNewAssemblyName('');
        setShowCreateAssembly(false);
        await fetchBom();
        setSelectedLine(line.line_id);
        setInsertionParentId(line.line_id); // Set as new insertion context
        setExpandedMap(prev => ({ ...prev, [line.line_id]: true }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create assembly');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBomBusy(null); }
  };

  // Enhancement 3b: Create Make Part inline
  const createMakePart = async () => {
    const finalName = selectedPartType
      ? `${selectedPartType.naming_prefix} for ${newMakePartPurpose.trim()}`
      : newMakePartName.trim();
    if (!finalName || (selectedPartType && !newMakePartPurpose.trim())) {
      toast.error(selectedPartType ? 'Purpose is required' : 'Part name is required');
      return;
    }
    const tk = getToken();
    if (!revision || bomBusy) return;
    setBomBusy('creating');
    try {
      const body = {
        bom_id: bomId,
        revision_id: revision.revision_id,
        part_name: finalName,
        description: newMakePartDesc.trim() || finalName,
        material_profile_id: newMakePartProfile || undefined,
        make_part_type_id: selectedPartType?.type_id || undefined,
        make_part_type_code: selectedPartType?.type_code || undefined,
        quantity: 1,
        parent_line_id: insertionParentId || undefined,
      };
      const res = await fetchWithAuth(`${API}/api/v1/bom/parts/make-inline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Make part "${finalName}" created (${result.part.hpn_compact})`);
        setNewMakePartName('');
        setNewMakePartDesc('');
        setNewMakePartProfile('');
        setSelectedPartType(null);
        setNewMakePartPurpose('');
        setShowCreateMakePart(false);
        await fetchBom();
        setSelectedLine(result.line.line_id);
        if (insertionParentId) setExpandedMap(prev => ({ ...prev, [insertionParentId]: true }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create Make part');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBomBusy(null); }
  };

  // Add fastener to BOM
  const handleAddFastener = async (payload) => {
    const tk = getToken();
    if (!revision) throw new Error('No active revision');
    payload.parent_line_id = insertionParentId || undefined;
    const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/fasteners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to add fastener');
    }
    const line = await res.json();
    await fetchBom();
    setSelectedLine(line.line_id);
    if (insertionParentId) setExpandedMap(prev => ({ ...prev, [insertionParentId]: true }));
  };

  // Apply a saved fastener set template
  const handleApplyFastenerTemplate = async (templateId, quantity) => {
    const tk = getToken();
    if (!revision) throw new Error('No active revision');
    const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/fastener-set-templates/${templateId}/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ quantity, parent_line_id: insertionParentId || undefined }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to apply template');
    }
    await fetchBom();
    if (insertionParentId) setExpandedMap(prev => ({ ...prev, [insertionParentId]: true }));
  };

  // Create a new part type inline
  const createNewPartType = async () => {
    if (!newPartTypeName.trim()) { toast.error('Part type name is required'); return; }
    const tk = getToken();
    try {
      const typeCode = newPartTypeName.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-');
      const body = {
        type_code: typeCode,
        type_name: newPartTypeName.trim(),
        description: newPartTypeDesc.trim() || newPartTypeName.trim(),
        naming_prefix: newPartTypeName.trim(),
        icon: "box",
        recommended_material_forms: ["*"],
        recommended_material_grades: ["*"],
        sort_order: 99,
      };
      const res = await fetchWithAuth(`${API}/api/v1/bom/make-part-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        toast.success(`Part type "${newPartTypeName}" created`);
        setMakePartTypes(prev => [...prev, created]);
        setSelectedPartType(created);
        setNewPartTypeName('');
        setNewPartTypeDesc('');
        setShowCreatePartType(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create part type');
      }
    } catch (e) { toast.error(e.message); }
  };

  const removeLine = async (lineId) => {
    const tk = getToken();
    if (!revision || bomBusy) return;
    setBomBusy('removing');
    try {
      await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines/${lineId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tk}` },
      });
      toast.success('Line removed');
      if (selectedLine === lineId) setSelectedLine(null);
      if (insertionParentId === lineId) setInsertionParentId(null);
      await fetchBom();
    } catch (e) { toast.error('Failed to remove'); }
    finally { setBomBusy(null); }
  };

  const updateLine = async (lineId, updates) => {
    const tk = getToken();
    if (!revision || bomBusy) return;
    setBomBusy('updating');
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines/${lineId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(updates),
      });
      if (res.ok) { await fetchBom(); toast.success('Updated', { duration: 2000 }); }
      else { const err = await res.json(); toast.error(err.detail || 'Update failed'); }
    } catch (e) { toast.error('Failed to update'); }
    finally { setBomBusy(null); }
  };

  // Enhancement 2: Preview assembly BOM lines
  const handlePreviewAssembly = async (part) => {
    if (previewAssembly?.hpn_compact === part.hpn_compact) {
      setPreviewAssembly(null);
      setPreviewLines([]);
      return;
    }
    setPreviewAssembly(part);
    setPreviewLines([]);
    setLoadingPreview(true);
    try {
      const tk = getToken();
      // Fetch the BOM header by the linked_bom_id to get its current revision
      const bomRes = await fetchWithAuth(`${API}/api/v1/bom/${part.linked_bom_id}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (bomRes.ok) {
        const bomData = await bomRes.json();
        if (bomData.current_revision_id) {
          const revRes = await fetchWithAuth(`${API}/api/v1/bom/${part.linked_bom_id}/revisions/${bomData.current_revision_id}`, { headers: { Authorization: `Bearer ${tk}` } });
          if (revRes.ok) {
            const revData = await revRes.json();
            setPreviewLines(revData.lines || []);
          }
        }
      }
    } catch (e) { console.error('Preview failed:', e); }
    setLoadingPreview(false);
  };

  // Enhancement 2: Expand/Collapse helpers
  const getAllNodeIds = useCallback(() => lines.map(l => l.line_id), [lines]);

  const handleToggleExpand = useCallback((nodeId) => {
    setExpandedMap(prev => ({ ...prev, [nodeId]: prev[nodeId] === false ? true : false }));
    setActiveLevel(null);
  }, []);

  const handleExpandAll = useCallback(() => {
    const map = {};
    lines.forEach(l => { map[l.line_id] = true; });
    setExpandedMap(map);
    setActiveLevel('all');
  }, [lines]);

  const handleCollapseAll = useCallback(() => {
    const map = {};
    lines.forEach(l => { map[l.line_id] = false; });
    setExpandedMap(map);
    setActiveLevel(0);
  }, [lines]);

  const handleExpandToLevel = useCallback((level) => {
    const map = {};
    lines.forEach(l => {
      map[l.line_id] = (l.level_depth || 0) < level;
    });
    setExpandedMap(map);
    setActiveLevel(level);
  }, [lines]);

  // Derived counts
  const selectedLineData = lines.find(l => l.line_id === selectedLine);
  const rootLines = lines.filter(l => !l.parent_line_id);
  const buyCount = lines.filter(l => l.make_buy === 'Buy').length;
  const makeCount = lines.filter(l => l.make_buy === 'Make').length;
  const rawCount = lines.filter(l => l.make_buy === 'Raw_Material').length;
  const consCount = lines.filter(l => l.make_buy === 'Consumable').length;
  const maxDepth = lines.reduce((max, l) => Math.max(max, l.level_depth || 0), 0);
  const assemblyCount = lines.filter(l => l.is_assembly).length;

  // Insertion context label
  const insertionLabel = useMemo(() => {
    if (!insertionParentId) return null;
    const parent = lines.find(l => l.line_id === insertionParentId);
    return parent ? (parent.assembly_number || parent.part_number || parent.part_id) : null;
  }, [insertionParentId, lines]);

  const approvalWorkflow = revision?.approval_workflow;
  const isDraft = revision?.status === 'Draft';
  const isInReview = revision?.status === 'InReview';
  const isReleased = revision?.status === 'Released';
  const currentStage = approvalWorkflow?.current_approval_stage;
  const canApproveCurrentStage = approvalPermissions?.allowed_stages?.[currentStage] ?? false;

  // Permission enforcement: derive edit capability from BOM ownership + role
  const isOwner = authUser?.user_id && bom?.owner_id === authUser.user_id;
  const isAdmin = authUser?.role === 'admin';
  const isEditor = authUser?.user_id && (bom?.editors || []).includes(authUser.user_id);
  const isViewer = authUser?.user_id && (bom?.viewers || []).includes(authUser.user_id);
  const canEdit = isDraft && (isOwner || isAdmin || isEditor);
  const showReadOnlyBanner = revision && !canEdit && !isInReview && !isReleased;

  // Keyboard shortcut: Ctrl+↑ / Ctrl+↓ to reorder selected tree row
  useEffect(() => {
    if (!canEdit || !selectedLine || !revision) return;
    const handleKeyboard = async (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();

      const line = lines.find(l => l.line_id === selectedLine);
      if (!line) return;

      const siblings = lines
        .filter(l => l.parent_line_id === line.parent_line_id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

      const idx = siblings.findIndex(s => s.line_id === selectedLine);
      if (idx < 0) return;

      const direction = e.key === 'ArrowUp' ? -1 : 1;
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= siblings.length) return;

      const newSortOrder = siblings[targetIdx].sort_order ?? targetIdx;
      try {
        const tk = getToken();
        const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines/reorder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ line_id: selectedLine, new_parent_line_id: line.parent_line_id || null, new_sort_order: newSortOrder }),
        });
        if (res.ok) await fetchBom();
      } catch (err) { /* silently fail keyboard reorder */ }
    };
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [canEdit, selectedLine, lines, revision, bomId, fetchBom]);

  // Approval handlers
  const handleSubmitForReview = async () => {
    setProcessingApproval(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ comments: approvalComment }),
      });
      if (res.ok) { toast.success('Submitted for review!'); setApprovalComment(''); setShowApprovalPanel(false); await fetchBom(); }
      else { const err = await res.json(); toast.error(err.detail || 'Submit failed'); }
    } catch (e) { toast.error(e.message); }
    finally { setProcessingApproval(false); }
  };

  const handleApprove = async () => {
    setProcessingApproval(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ decision: 'Approved', comments: approvalComment, meaning_statement: 'I have reviewed this BOM revision and confirm it meets requirements.' }),
      });
      if (res.ok) { const d = await res.json(); toast.success(`Approved! Status: ${d.status}`); setApprovalComment(''); setShowApprovalPanel(false); await fetchBom(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    finally { setProcessingApproval(false); }
  };

  const handleReject = async () => {
    if (rejectReason.length < 10) { toast.error('Reason must be ≥10 characters'); return; }
    setProcessingApproval(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ rejection_reason: rejectReason }),
      });
      if (res.ok) { toast.success('Rejected → Draft'); setRejectReason(''); setShowRejectForm(false); setShowApprovalPanel(false); await fetchBom(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    finally { setProcessingApproval(false); }
  };

  const handleReturnToDraft = async () => {
    setProcessingApproval(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ decision: 'ReturnedToDraft', comments: approvalComment }),
      });
      if (res.ok) { toast.success('Returned to draft'); setApprovalComment(''); setShowApprovalPanel(false); await fetchBom(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error(e.message); }
    finally { setProcessingApproval(false); }
  };

  if (!bom) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-500" data-bom-theme={theme}>Loading BOM...</div>;

  // Drag-and-drop callbacks for DragProvider
  const handleDragAddPart = async (part, parentId, sortOrder) => {
    const tk = getToken();
    if (!revision) return;
    try {
      const body = {
        part_id: part.hpn_compact,
        quantity: 1,
        make_buy: addClassification,
        parent_line_id: parentId || undefined,
      };
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const line = await res.json();
        toast.success(`${part.hpn_compact || part.hpn_display} added via drag`);
        await fetchBom();
        setSelectedLine(line.line_id);
        if (parentId) setExpandedMap(prev => ({ ...prev, [parentId]: true }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add');
      }
    } catch (e) { toast.error(e.message); }
  };

  const handleDragReorder = async (lineId, newParentId, newSortOrder) => {
    const tk = getToken();
    if (!revision) return;
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision.revision_id}/lines/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ line_id: lineId, new_parent_line_id: newParentId, new_sort_order: newSortOrder }),
      });
      if (res.ok) {
        toast.success('Reordered');
        await fetchBom();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Reorder failed');
      }
    } catch (e) { toast.error(e.message); }
  };

  return (
    <DragProvider lines={lines} onAddPart={handleDragAddPart} onReorderLine={handleDragReorder} isDraft={canEdit}>
    <div className="h-full flex flex-col bg-slate-950 text-white overflow-hidden" data-bom-theme={theme}>
      {/* Header Bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-900 border-b border-slate-700/50 shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate('/bom')} className="text-slate-400 h-8 w-8">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2 min-w-0">
          <Layers className="w-4 h-4 text-cyan-400 shrink-0" />
          <span className="font-mono font-bold text-cyan-400">{bom.bom_number}</span>
          <span className="text-slate-300 truncate">{bom.product_name}</span>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowRevisionPanel(p => !p)}
            className="flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-[10px] px-2 py-1 rounded shrink-0 transition-colors"
            data-testid="revision-selector-btn"
          >
            <GitBranch className="w-3 h-3" />
            Rev {revision?.revision_number || '—'}
            <ChevronDown className="w-2.5 h-2.5 ml-0.5" />
          </button>
          {showRevisionPanel && (
            <div className="absolute top-full left-0 mt-1 z-50 w-72 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden" data-testid="revision-panel">
              <div className="px-3 py-2 border-b border-slate-700 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-slate-300 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Revisions</span>
                <button
                  onClick={() => { setShowNewRevisionDialog(true); setShowRevisionPanel(false); }}
                  className="flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300 transition-colors"
                  data-testid="new-revision-btn"
                >
                  <Copy className="w-3 h-3" /> New Revision
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {revisionList.length === 0 && <div className="px-3 py-4 text-center text-[10px] text-slate-500">No revisions</div>}
                {[...revisionList].sort((a, b) => (b.revision_major || '').localeCompare(a.revision_major || '') || (b.revision_minor || 0) - (a.revision_minor || 0)).map(rev => {
                  const isCurrent = rev.revision_id === revision?.revision_id;
                  const statusColor = rev.status === 'Released' ? 'text-emerald-400' : rev.status === 'InReview' ? 'text-amber-400' : rev.status === 'Superseded' ? 'text-slate-500' : 'text-blue-400';
                  return (
                    <button
                      key={rev.revision_id}
                      onClick={() => switchRevision(rev.revision_id)}
                      className={`w-full text-left px-3 py-2 text-[11px] transition-colors border-b border-slate-700/50 last:border-0 ${isCurrent ? 'bg-cyan-900/20 text-white' : 'text-slate-300 hover:bg-slate-700/50'}`}
                      data-testid={`revision-item-${rev.revision_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold">Rev {rev.revision_number}</span>
                        <span className={`text-[9px] ${statusColor}`}>{rev.status}</span>
                      </div>
                      {rev.change_reason && <div className="text-[9px] text-slate-500 mt-0.5 truncate">{rev.change_reason}</div>}
                      <div className="text-[9px] text-slate-600 mt-0.5">{rev.lines?.length || 0} lines{rev.copied_from_revision_number ? ` · from Rev ${rev.copied_from_revision_number}` : ''}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <Badge className={`text-[10px] shrink-0 cursor-pointer transition-all hover:scale-105 ${
          isReleased ? 'bg-emerald-900/30 text-emerald-400 border-emerald-600/30' :
          isInReview ? 'bg-amber-900/30 text-amber-400 border-amber-600/30' : 'bg-slate-600 text-slate-300'
        } border`} onClick={() => setShowApprovalPanel(!showApprovalPanel)}>
          {isReleased ? <CheckCircle className="w-3 h-3 mr-1" /> : isInReview ? <Clock className="w-3 h-3 mr-1" /> : null}
          {revision?.status || '—'}
          {isInReview && currentStage && <span className="ml-1 text-[9px] opacity-70">({currentStage === 'Engineering_Lead' ? 'Eng' : 'QA'})</span>}
        </Badge>

        {/* Approval Actions */}
        <div className="flex items-center gap-2">
          {canEdit && lines.length > 0 && (
            <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-500" onClick={() => setShowApprovalPanel(!showApprovalPanel)}>
              <Send className="w-3 h-3 mr-1" /> Submit for Review
            </Button>
          )}
          {isInReview && canApproveCurrentStage && (
            <>
              <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-500" onClick={() => setShowApprovalPanel(!showApprovalPanel)}>
                <CheckCircle className="w-3 h-3 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-400 border-red-600 hover:bg-red-900/20"
                onClick={() => { setShowApprovalPanel(true); setShowRejectForm(true); }}>
                <XCircle className="w-3 h-3 mr-1" /> Reject
              </Button>
            </>
          )}
          {isInReview && !canApproveCurrentStage && (
            <Badge className="bg-slate-700/50 text-slate-400 text-[10px] border border-slate-600">
              <Clock className="w-3 h-3 mr-1" />Awaiting {currentStage === 'Engineering_Lead' ? 'Engineering' : 'Quality'}
            </Badge>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] text-slate-500">
            Lines: {lines.length} │ Buy: {buyCount} Make: {makeCount} Assy: {assemblyCount} Cons: {consCount}
          </span>
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-md transition-colors bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            data-testid="bom-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Read-only Mode Banner */}
      {showReadOnlyBanner && (
        <div className="flex items-center justify-center gap-2 px-4 py-1.5 bg-amber-900/30 border-b border-amber-700/40 shrink-0" data-testid="read-only-banner">
          <Eye className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-[11px] font-semibold text-amber-300">Read-only Mode</span>
          <span className="text-[10px] text-amber-400/70">&mdash; You do not have edit access to this BOM. Contact the owner for editor permissions.</span>
        </div>
      )}

      {/* New Revision Dialog */}
      {showNewRevisionDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !creatingRevision && setShowNewRevisionDialog(false)}>
          <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-2xl w-96 p-4" onClick={e => e.stopPropagation()} data-testid="new-revision-dialog">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
              <Copy className="w-4 h-4 text-cyan-400" />
              Create New Revision
            </h3>
            <div className="text-[11px] text-slate-400 mb-3 bg-slate-900/50 rounded p-2">
              A new <span className="text-cyan-300 font-mono">Draft</span> revision will be created by copying all <span className="text-white font-semibold">{lines.length} lines</span> from <span className="text-white font-mono">Rev {revision?.revision_number}</span>.
              {(revision?.status === 'Released' || revision?.status === 'Approved') && (
                <span className="block mt-1 text-amber-400">Rev {revision?.revision_number} will be marked as Superseded.</span>
              )}
            </div>
            <Label className="text-[10px] text-slate-400 mb-1 block">Change Reason</Label>
            <Input
              value={newRevisionReason}
              onChange={e => setNewRevisionReason(e.target.value)}
              placeholder="e.g. Engineering change order #42, material substitution..."
              className="h-8 text-xs bg-slate-900 border-slate-600 text-white mb-3"
              data-testid="new-revision-reason"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-400" onClick={() => setShowNewRevisionDialog(false)} disabled={creatingRevision}>
                Cancel
              </Button>
              <Button size="sm" className="h-7 text-[10px] bg-cyan-600 hover:bg-cyan-500" onClick={createNewRevision} disabled={creatingRevision} data-testid="confirm-new-revision">
                {creatingRevision ? <><span className="animate-spin mr-1">&#9696;</span>Creating...</> : <><Copy className="w-3 h-3 mr-1" /> Create Revision</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Panel */}
      {showApprovalPanel && (
        <div className="bg-slate-800/95 border-b border-slate-700 px-4 py-3 animate-in slide-in-from-top-2 duration-200">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-400" />
                {isDraft ? 'Submit for Review' : isInReview ? 'Approval Workflow' : 'Status'}
              </h3>
              <Button variant="ghost" size="sm" className="h-6 text-slate-400" onClick={() => { setShowApprovalPanel(false); setShowRejectForm(false); }}>✕</Button>
            </div>
            {/* Workflow Progress */}
            <div className="flex items-center gap-2 mb-4">
              {[
                { label: 'Draft', icon: FileText, active: isDraft },
                { label: 'Engineering', icon: User, active: isInReview && currentStage === 'Engineering_Lead', done: (isInReview && currentStage === 'Quality_Rep') || isReleased },
                { label: 'Quality', icon: Shield, active: isInReview && currentStage === 'Quality_Rep', done: isReleased },
                { label: 'Released', icon: CheckCircle, active: isReleased },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  {i > 0 && <ChevronRight className="w-4 h-4 text-slate-600" />}
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] ${
                    step.active ? (step.label === 'Released' ? 'bg-emerald-600' : 'bg-blue-600') + ' text-white' :
                    step.done ? 'bg-blue-900/50 text-blue-400' : 'bg-slate-700/50 text-slate-500'
                  }`}>
                    <step.icon className="w-3 h-3" /> {step.label}
                  </div>
                </React.Fragment>
              ))}
            </div>
            {/* Approval History */}
            {approvalWorkflow?.approvals?.length > 0 && (
              <div className="mb-4 p-2 bg-slate-900/50 rounded border border-slate-700/50">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">History</div>
                {approvalWorkflow.approvals.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] py-1">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      a.decision === 'Approved' ? 'bg-emerald-500/20 text-emerald-400' : a.decision === 'Rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {a.decision === 'Approved' ? <CheckCircle className="w-3 h-3" /> : a.decision === 'Rejected' ? <XCircle className="w-3 h-3" /> : <RotateCcw className="w-3 h-3" />}
                    </span>
                    <span className="text-slate-300">{a.decision}</span>
                    <span className="text-slate-500">by {a.performed_by_name}</span>
                    <span className="text-slate-600 ml-auto">{new Date(a.performed_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Action Forms */}
            {!isReleased && !showRejectForm && (
              <div className="space-y-3">
                {isInReview && !canApproveCurrentStage && (
                  <div className="flex items-center gap-2 p-2 bg-amber-900/20 border border-amber-700/30 rounded text-[11px]">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-300">No permission for {currentStage === 'Engineering_Lead' ? 'Engineering' : 'Quality'} stage.</span>
                  </div>
                )}
                <div>
                  <Label className="text-[10px] text-slate-400 mb-1 block">Comments</Label>
                  <textarea value={approvalComment} onChange={(e) => setApprovalComment(e.target.value)}
                    placeholder={isDraft ? "Submission comments..." : "Approval comments..."}
                    className="w-full bg-slate-900/50 border border-slate-700 rounded px-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-blue-500"
                    rows={2} />
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && <Button onClick={handleSubmitForReview} disabled={processingApproval || lines.length === 0} className="bg-blue-600 hover:bg-blue-500 text-white text-[11px] h-8"><Send className="w-3 h-3 mr-1" />{processingApproval ? 'Submitting...' : 'Submit'}</Button>}
                  {isInReview && canApproveCurrentStage && (
                    <>
                      <Button onClick={handleApprove} disabled={processingApproval} className="bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] h-8"><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
                      <Button onClick={handleReturnToDraft} disabled={processingApproval} variant="outline" className="text-amber-400 border-amber-600 hover:bg-amber-900/20 text-[11px] h-8"><RotateCcw className="w-3 h-3 mr-1" />Return</Button>
                      <Button onClick={() => setShowRejectForm(true)} variant="outline" className="text-red-400 border-red-600 hover:bg-red-900/20 text-[11px] h-8"><XCircle className="w-3 h-3 mr-1" />Reject</Button>
                    </>
                  )}
                </div>
              </div>
            )}
            {showRejectForm && canApproveCurrentStage && (
              <div className="space-y-3 p-3 bg-red-900/10 border border-red-700/30 rounded">
                <div className="text-sm font-medium text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Reject</div>
                <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (min 10 chars)..."
                  className="w-full bg-slate-900/50 border border-red-700/50 rounded px-3 py-2 text-xs text-slate-200 placeholder-red-400/50 focus:ring-2 focus:ring-red-500" rows={3} />
                <div className="text-[10px] text-slate-500">{rejectReason.length}/10 min</div>
                <div className="flex gap-2">
                  <Button onClick={handleReject} disabled={processingApproval || rejectReason.length < 10} className="bg-red-600 hover:bg-red-500 text-white text-[11px] h-8">Confirm Reject</Button>
                  <Button onClick={() => { setShowRejectForm(false); setRejectReason(''); }} variant="ghost" className="text-slate-400 text-[11px] h-8">Cancel</Button>
                </div>
              </div>
            )}
            {isReleased && (
              <div className="flex items-center gap-3 p-3 bg-emerald-900/10 border border-emerald-700/30 rounded">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
                <div>
                  <div className="text-sm font-medium text-emerald-400">Released</div>
                  <div className="text-[11px] text-slate-400">by {approvalWorkflow?.released_by_name}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost & Weight Bar */}
      {activeLibraryTab === 'product_builder' && (
      <CostBar cost={revision?.cost_total || 0} budget={bom.budget_target || 0}
        weight={revision?.cost_breakdown?.total_weight_kg || 0} weightBudget={bom.weight_budget_kg || 0}
        description={bom.description || ''}
        bomId={bomId} revisionId={revision?.revision_id} onRefresh={fetchBom} />
      )}

      {/* Breadcrumb Bar - Enhancement 3 */}
      {activeLibraryTab === 'product_builder' && (
      <BreadcrumbBar lines={lines} selectedLineId={selectedLine} productName={bom.product_name} onSelect={handleSelectLine} />
      )}

      {/* === LIBRARY TAB CONTENT SWITCH === */}
      {activeLibraryTab === 'product_builder' ? (
      /* Three-Panel Workspace */
      <div className="flex flex-1 overflow-hidden">
        {/* Panel A: Search */}
        {!searchCollapsed ? (
          <div className="w-[280px] border-r border-slate-700/50 flex flex-col shrink-0 bg-slate-900/50">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Parts Search</span>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500" onClick={() => setSearchCollapsed(true)}>
                <PanelLeftClose className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Insertion context indicator */}
            {insertionLabel && (
              <div className="px-2 py-1.5 bg-cyan-900/20 border-b border-cyan-700/30 flex items-center gap-1.5 text-[10px]">
                <span className="text-cyan-400">Adding to:</span>
                <span className="text-white font-mono font-medium">◆ {insertionLabel}</span>
                <button onClick={() => setInsertionParentId(null)} className="ml-auto text-slate-500 hover:text-white text-[10px]">✕ Root</button>
              </div>
            )}

            <div className="p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <Input placeholder="Search parts... (part#, desc, OEM, MPN, category)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-xs bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" />
              </div>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-[10px] text-slate-500">Add as:</span>
                {['Buy', 'Make', 'Consumable'].map(c => (
                  <button key={c} onClick={() => setAddClassification(c)}
                    className={`px-1.5 py-0.5 rounded text-[10px] transition-colors ${addClassification === c ? 'bg-blue-900/40 text-blue-400 border border-blue-600/30' : 'text-slate-500 hover:text-slate-300'}`}>
                    <ClassIcon type={c} size={10} /> {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {searching && <div className="text-center text-slate-500 text-[10px] py-4">Searching...</div>}
              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="text-center text-slate-500 text-[10px] py-4">
                  No parts matching "{searchQuery}"
                  <div className="flex flex-col items-center gap-1.5 mt-3">
                    {canEdit && (
                      <button onClick={() => { setShowCreateMakePart(true); setShowCreateAssembly(false); setNewMakePartName(searchQuery); setSelectedPartType(null); setNewMakePartPurpose(''); }}
                        className="text-blue-400 hover:text-blue-300 underline">
                        + Create Make Part
                      </button>
                    )}
                    <span className="text-slate-600">or try different search terms</span>
                  </div>
                </div>
              )}
              {searchResults.map(part => (
                <React.Fragment key={part.hpn_compact}>
                  <SearchCard part={part} onAdd={addPart} onPreview={handlePreviewAssembly} />
                  {/* Assembly Preview Panel */}
                  {previewAssembly?.hpn_compact === part.hpn_compact && (
                    <div className="ml-2 mb-1 border-l-2 border-blue-600/40 bg-blue-950/20 rounded-r-lg p-2 animate-in slide-in-from-top-1 duration-150"
                      data-testid={`preview-panel-${part.hpn_compact}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-blue-400 font-semibold">BOM Preview</span>
                        <button onClick={() => { setPreviewAssembly(null); setPreviewLines([]); }}
                          className="text-[10px] text-slate-500 hover:text-white">✕</button>
                      </div>
                      {loadingPreview ? (
                        <div className="text-[10px] text-slate-500 animate-pulse py-2 text-center">Loading...</div>
                      ) : previewLines.length === 0 ? (
                        <div className="text-[10px] text-slate-600 py-1 italic">No lines in this BOM</div>
                      ) : (
                        <div className="space-y-0.5 max-h-[180px] overflow-y-auto">
                          {previewLines.filter(l => !l.parent_line_id).map((l, i) => (
                            <div key={l.line_id || i} className="flex items-center gap-1 text-[10px] py-0.5">
                              <span className="shrink-0">{l.is_assembly ? <AssemblyIcon size={10} /> : <ClassIcon type={l.make_buy} size={10} />}</span>
                              <span className="font-mono text-slate-400">{l.is_assembly ? l.assembly_number : l.part_number || l.part_id}</span>
                              <span className="text-slate-500 truncate flex-1 min-w-0">{l.is_assembly ? l.assembly_name : (l.part_name || l.part_description)}</span>
                              <span className="text-slate-600 font-mono shrink-0">x{l.quantity}</span>
                            </div>
                          ))}
                          {previewLines.filter(l => l.parent_line_id).length > 0 && (
                            <div className="text-[9px] text-slate-600 pt-0.5 border-t border-slate-700/30">
                              + {previewLines.filter(l => l.parent_line_id).length} nested items
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {searchQuery.length < 2 && (
                <div className="text-center text-slate-600 text-[10px] py-8">
                  Type 2+ characters to search<br />
                  <span className="text-slate-700">Searches: part#, description, OEM, MPN, category</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="w-[40px] border-r border-slate-700/50 flex flex-col items-center py-2 bg-slate-900/50 shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500" onClick={() => setSearchCollapsed(false)}>
              <Search className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {/* Panel B: BOM Tree */}
        <div className="flex-1 flex flex-col min-w-[320px] border-r border-slate-700/50">
          {/* Phase 6.1: Thin progress bar — shows during any BOM mutation */}
          {bomBusy && (
            <div data-testid="bom-busy-bar" className="h-0.5 w-full bg-slate-800 overflow-hidden shrink-0 relative">
              <div className="h-full bg-amber-400/80 absolute" style={{width:'30%',animation:'bomSlide 0.8s ease-in-out infinite alternate'}}/>
              <style>{`@keyframes bomSlide{0%{left:0;width:30%}100%{left:70%;width:30%}}`}</style>
            </div>
          )}
          {/* Tree Header with Expand/Collapse controls */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 bg-slate-900/30">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">BOM Structure</span>
              {bomBusy && <span data-testid="bom-busy-label" className="text-[9px] text-amber-400 animate-pulse font-mono">{bomBusy}...</span>}
            </div>
            <div className="flex items-center gap-1">
              {/* Enhancement 3: Create Assembly button */}
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-blue-400 hover:bg-blue-900/20"
                    onClick={() => { setShowCreateMakePart(!showCreateMakePart); setShowCreateAssembly(false); setShowFastenerSelector(false); setSelectedPartType(null); setNewMakePartPurpose(''); setShowCreatePartType(false); }}
                    data-testid="create-make-part-btn">
                    <Square className="w-3 h-3 mr-0.5" /> + Make Part
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-amber-400 hover:bg-amber-900/20"
                    onClick={() => { setShowFastenerSelector(!showFastenerSelector); setShowCreateMakePart(false); setShowCreateAssembly(false); }}
                    data-testid="create-fastener-btn">
                    <Wrench className="w-3 h-3 mr-0.5" /> + Fastener
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-cyan-400 hover:bg-cyan-900/20"
                    onClick={() => { setShowCreateAssembly(!showCreateAssembly); setShowCreateMakePart(false); setShowFastenerSelector(false); }}>
                    <FolderPlus className="w-3 h-3 mr-0.5" /> + Assembly
                  </Button>
                </>
              )}
              <div className="w-px h-4 bg-slate-700 mx-1" />
              {/* Enhancement 2: Expand/Collapse buttons */}
              <Button variant="ghost" size="sm" onClick={handleExpandAll}
                className={`h-6 px-2 text-[10px] ${activeLevel === 'all' ? 'text-blue-400 bg-blue-900/20' : 'text-slate-400'}`}
                data-testid="expand-all-btn">
                <ChevronsUpDown className="w-3 h-3 mr-0.5" /> Expand
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCollapseAll}
                className={`h-6 px-2 text-[10px] ${activeLevel === 0 ? 'text-blue-400 bg-blue-900/20' : 'text-slate-400'}`}
                data-testid="collapse-all-btn">
                <ChevronsDownUp className="w-3 h-3 mr-0.5" /> Collapse
              </Button>
              {maxDepth > 0 && (
                <>
                  <div className="w-px h-4 bg-slate-700 mx-1" />
                  <span className="text-[9px] text-slate-600 mr-1">Level:</span>
                  {Array.from({ length: Math.min(maxDepth + 1, 6) }, (_, i) => i + 1).map(lvl => (
                    <button key={lvl} onClick={() => handleExpandToLevel(lvl)}
                      className={`w-5 h-5 rounded text-[10px] font-mono ${activeLevel === lvl ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/50'}`}>
                      {lvl}
                    </button>
                  ))}
                </>
              )}
              <div className="w-px h-4 bg-slate-700 mx-1" />
              {/* Re-Calculate button */}
              <Button variant="ghost" size="sm" onClick={async () => {
                setBomBusy('recalculating');
                try {
                  const tk = getToken();
                  const res = await fetchWithAuth(`${API}/api/v1/bom/${bomId}/revisions/${revision?.revision_id}/recalculate`, {
                    method: 'POST', headers: { Authorization: `Bearer ${tk}` },
                  });
                  if (res.ok) { await fetchBom(); toast.success('BOM recalculated'); }
                  else { const e = await res.json(); toast.error(e.detail || 'Recalculate failed'); }
                } catch { toast.error('Recalculate failed'); }
                setBomBusy(null);
              }}
                className="h-6 px-2 text-[10px] text-emerald-400 hover:bg-emerald-900/20"
                data-testid="recalculate-btn"
                disabled={!!bomBusy}
              >
                <Calculator className="w-3 h-3 mr-0.5" /> Re-Calculate
              </Button>
              <div className="w-px h-4 bg-slate-700 mx-1" />
              {/* Enhancement 4: Compact/Comfortable toggle */}
              <button onClick={() => setTreeViewMode(m => m === 'compact' ? 'comfortable' : 'compact')}
                className={`h-6 px-1.5 rounded text-[10px] flex items-center gap-0.5 transition-colors ${treeViewMode === 'comfortable' ? 'text-blue-400 bg-blue-900/20' : 'text-slate-500 hover:text-slate-300'}`}
                title={treeViewMode === 'compact' ? 'Switch to Comfortable (two-line rows)' : 'Switch to Compact (single-line rows)'}
                data-testid="tree-view-mode-toggle">
                {treeViewMode === 'compact' ? <List className="w-3 h-3" /> : <AlignJustify className="w-3 h-3" />}
              </button>
            </div>
          </div>

          {/* Create Assembly Inline Form */}
          {showCreateAssembly && (
            <div className="px-3 py-2 bg-blue-900/10 border-b border-blue-700/30 animate-in fade-in duration-150">
              <div className="text-[10px] text-blue-400 font-semibold mb-1.5">CREATE NEW ASSEMBLY {insertionLabel ? `under ◆ ${insertionLabel}` : '(at root)'}</div>
              <div className="flex items-center gap-2">
                <Input value={newAssemblyName} onChange={e => setNewAssemblyName(e.target.value)}
                  placeholder="Assembly name (e.g., Sensor Plate Sub-Assy)"
                  className="h-7 text-xs bg-slate-800 border-blue-600/50 text-white flex-1 focus:border-blue-400"
                  onKeyDown={e => { if (e.key === 'Enter') createAssembly(); if (e.key === 'Escape') setShowCreateAssembly(false); }} />
                <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-500" onClick={createAssembly} disabled={bomBusy || !newAssemblyName.trim()}>
                  {bomBusy === 'creating' ? <><span className="animate-spin mr-1">&#9696;</span>Creating...</> : <><Plus className="w-3 h-3 mr-0.5" /> Create</>}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400" onClick={() => { setShowCreateAssembly(false); setNewAssemblyName(''); }}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Enhancement 3b: Create Make Part Inline Form — with Part Type Catalog */}
          {showCreateMakePart && (
            <div className="px-3 py-2.5 bg-blue-900/10 border-b border-blue-700/30 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 mb-2">
                <Square className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider">Create Make Part</span>
                <span className="text-[10px] text-slate-500">{insertionLabel ? `under ◆ ${insertionLabel}` : '(at root)'}</span>
                <span className="text-[9px] text-amber-600/60 ml-auto">[8.1.2]</span>
              </div>
              <div className="space-y-2">
                {/* Part Type Selector */}
                {!showCreatePartType ? (
                  <div>
                    <Label className="text-[9px] text-slate-500 mb-1 block uppercase tracking-wider">Part Type</Label>
                    <Input
                      value={makePartTypeSearch}
                      onChange={e => setMakePartTypeSearch(e.target.value)}
                      placeholder="Search part types..."
                      className="h-6 text-[10px] bg-slate-800 border-slate-700/50 text-white mb-1.5 focus:border-blue-500"
                      data-testid="make-part-type-search"
                    />
                    <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto pr-1" data-testid="make-part-type-grid">
                      {makePartTypes
                        .filter(pt => !makePartTypeSearch.trim() || pt.type_name.toLowerCase().includes(makePartTypeSearch.toLowerCase()))
                        .map(pt => (
                        <button key={pt.type_id} onClick={() => { setSelectedPartType(pt); setNewMakePartPurpose(''); setMakePartTypeSearch(''); }}
                          className={`text-left px-2 py-1.5 rounded border text-[10px] transition-all ${
                            selectedPartType?.type_id === pt.type_id
                              ? 'border-blue-500 bg-blue-900/40 text-blue-300 ring-1 ring-blue-500/30'
                              : 'border-slate-700/40 bg-slate-800/30 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                          }`}
                          data-testid={`make-part-type-${pt.type_code}`}>
                          <div className="font-medium truncate">{pt.type_name}</div>
                          {selectedPartType?.type_id === pt.type_id && pt.description && (
                            <div className="text-[8px] text-blue-400/70 mt-0.5 truncate">{pt.description}</div>
                          )}
                        </button>
                      ))}
                      <button onClick={() => setShowCreatePartType(true)}
                        className="px-2 py-1.5 rounded border border-dashed border-blue-600/30 text-[10px] text-blue-400/70 hover:text-blue-300 hover:border-blue-500/50 transition-colors flex items-center gap-1 justify-center"
                        data-testid="create-new-part-type-btn">
                        <Plus className="w-3 h-3" /> New Type
                      </button>
                    </div>
                    {selectedPartType && (
                      <div className="mt-1 text-[9px] text-slate-500 flex items-center gap-1">
                        <span className="text-blue-400">{selectedPartType.type_name}</span>
                        {selectedPartType.recommended_material_forms?.[0] !== '*' && (
                          <span className="text-slate-600">│ Recommended: {selectedPartType.recommended_material_forms?.join(', ')}</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border border-blue-600/30 rounded p-2 bg-slate-900/50" data-testid="create-part-type-form">
                    <div className="text-[9px] text-blue-400 font-semibold mb-1.5 uppercase tracking-wider">New Part Type</div>
                    <Input value={newPartTypeName} onChange={e => setNewPartTypeName(e.target.value)}
                      placeholder="Type name (e.g., Manifold Block)"
                      className="h-7 text-xs bg-slate-800 border-slate-600 text-white mb-1.5"
                      onKeyDown={e => { if (e.key === 'Enter' && newPartTypeName.trim()) createNewPartType(); if (e.key === 'Escape') setShowCreatePartType(false); }}
                      data-testid="new-part-type-name" autoFocus />
                    <Input value={newPartTypeDesc} onChange={e => setNewPartTypeDesc(e.target.value)}
                      placeholder="Description (optional)"
                      className="h-7 text-xs bg-slate-800 border-slate-700 text-white mb-1.5" />
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-6 text-[10px] bg-blue-600 hover:bg-blue-500" onClick={createNewPartType} disabled={!newPartTypeName.trim()}
                        data-testid="submit-new-part-type">
                        <Plus className="w-3 h-3 mr-0.5" /> Create Type
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-400"
                        onClick={() => { setShowCreatePartType(false); setNewPartTypeName(''); setNewPartTypeDesc(''); }}>Cancel</Button>
                    </div>
                  </div>
                )}

                {/* Naming — conditional on whether type is selected */}
                {selectedPartType ? (
                  <div className="bg-slate-900/50 rounded p-2 border border-blue-600/20" data-testid="make-part-naming-section">
                    <Label className="text-[9px] text-slate-500 mb-1 block uppercase tracking-wider">Part Name</Label>
                    <div className="flex items-center gap-0">
                      <span className="text-xs text-blue-300 font-medium whitespace-nowrap bg-blue-900/30 border border-blue-600/30 rounded-l px-2 py-1.5 border-r-0">
                        {selectedPartType.naming_prefix} for
                      </span>
                      <Input value={newMakePartPurpose} onChange={e => setNewMakePartPurpose(e.target.value)}
                        placeholder="Solenoid Valve, Ball Valve, Control Panel..."
                        className="h-[34px] text-xs bg-slate-800 border-blue-600/30 text-white rounded-l-none focus:border-blue-400 flex-1"
                        onKeyDown={e => { if (e.key === 'Enter' && newMakePartPurpose.trim()) createMakePart(); if (e.key === 'Escape') setShowCreateMakePart(false); }}
                        data-testid="make-part-purpose-input" autoFocus />
                    </div>
                    {newMakePartPurpose.trim() && (
                      <div className="mt-1 text-[9px] text-slate-500">
                        Result: <span className="text-cyan-400 font-medium">{selectedPartType.naming_prefix} for {newMakePartPurpose.trim()}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Label className="text-[9px] text-slate-500 mb-1 block uppercase tracking-wider">Or enter name directly</Label>
                    <Input value={newMakePartName} onChange={e => setNewMakePartName(e.target.value)}
                      placeholder="Part name (e.g., Main Mounting Bracket)"
                      className="h-7 text-xs bg-slate-800 border-blue-600/50 text-white focus:border-blue-400"
                      onKeyDown={e => { if (e.key === 'Enter' && newMakePartName.trim()) createMakePart(); if (e.key === 'Escape') setShowCreateMakePart(false); }}
                      data-testid="make-part-name-input" />
                  </div>
                )}

                <Input value={newMakePartDesc} onChange={e => setNewMakePartDesc(e.target.value)}
                  placeholder="Description (optional — e.g., SS316L, 3mm, Sensor Mount Plate)"
                  className="h-7 text-xs bg-slate-800 border-slate-700 text-white focus:border-blue-400"
                  data-testid="make-part-desc-input" />

                <div className="flex items-center gap-2">
                  <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-500"
                    onClick={createMakePart}
                    disabled={bomBusy || (selectedPartType ? !newMakePartPurpose.trim() : !newMakePartName.trim())}
                    data-testid="create-make-part-submit">
                    {bomBusy === 'creating' ? <><span className="animate-spin mr-1">&#9696;</span>Creating...</> : <><Plus className="w-3 h-3 mr-0.5" /> Create</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400"
                    onClick={() => { setShowCreateMakePart(false); setNewMakePartName(''); setNewMakePartDesc(''); setNewMakePartProfile(''); setSelectedPartType(null); setNewMakePartPurpose(''); setShowCreatePartType(false); setMakePartTypeSearch(''); }}>Cancel</Button>
                </div>
                <div className="text-[9px] text-slate-600 flex items-center gap-1">
                  <span>Part number auto-generated from shared pool</span>
                  <span className="text-slate-700">│</span>
                  <span>Part registered in catalog with origin: BOM-Created</span>
                </div>
              </div>
            </div>
          )}

          {/* Fastener Selector Panel */}
          {showFastenerSelector && (
            <FastenerSelector
              onAddFastener={handleAddFastener}
              onApplyTemplate={handleApplyFastenerTemplate}
              onClose={() => setShowFastenerSelector(false)}
              insertionLabel={insertionLabel ? `${insertionLabel}` : null}
            />
          )}

          <TreeScrollContainer>
            {/* Root node */}
            <div className={`flex items-center gap-1 py-2 px-3 border-b border-slate-700/30 text-xs cursor-pointer transition-colors ${
              !insertionParentId ? 'bg-slate-800/50' : 'bg-slate-800/20 hover:bg-slate-800/30'
            }`} onClick={() => { setInsertionParentId(null); setSelectedLine(null); }}>
              <Diamond className="w-3.5 h-3.5 text-blue-400" />
              <span className="font-semibold text-white">{bom.product_name}</span>
              <span className="text-[10px] text-slate-600 ml-1">(root)</span>
              {bom.description && <span className="text-[10px] text-slate-500 ml-1 truncate max-w-[300px]">{bom.description}</span>}
              <span className="ml-auto font-mono text-blue-400/60 text-[10px]">{(revision?.cost_breakdown?.total_weight_kg || 0).toFixed(2)} kg</span>
              <span className="font-mono text-slate-400 ml-1">₹{(revision?.cost_total || 0).toLocaleString()}</span>
              {!insertionParentId && <span className="text-[9px] text-cyan-500 ml-1">← adding here</span>}
            </div>

            {rootLines.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-xs gap-2">
                <Layers className="w-8 h-8 text-slate-700" />
                <span>No parts yet.</span>
                <span className="text-slate-600">Search and add parts, or create an assembly.</span>
              </div>
            ) : (
              rootLines.map((line, idx) => (
                <TreeNode key={line.line_id} line={line} lines={lines} selected={selectedLine}
                  onSelect={handleSelectLine} depth={0} expandedMap={expandedMap}
                  onToggleExpand={handleToggleExpand} insertionParentId={insertionParentId} treeViewMode={treeViewMode}
                  isLastChild={idx === rootLines.length - 1} ancestorContinues={[]}
                  selectedLineIds={selectedLineIds} onToggleLineSelect={handleToggleLineSelect} isDraft={canEdit} />
              ))
            )}
          </TreeScrollContainer>
        </div>

        {/* Panel C: Detail / Activity */}
        <div className="w-[380px] shrink-0 bg-slate-900/30 flex flex-col overflow-hidden">
          <div className="flex items-center px-3 py-0 border-b border-slate-700/50">
            <button
              onClick={() => setDetailTab('detail')}
              className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold transition-colors border-b-2 ${
                detailTab === 'detail'
                  ? 'text-white border-blue-500'
                  : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
              data-testid="detail-tab-detail"
            >
              Detail
            </button>
            <button
              onClick={() => setDetailTab('activity')}
              className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold transition-colors border-b-2 ${
                detailTab === 'activity'
                  ? 'text-white border-blue-500'
                  : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
              data-testid="detail-tab-activity"
            >
              Activity
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            {isMultiSelectActive ? (
              <BatchDetailPanel
                selectedLineIds={selectedLineIds}
                lines={lines}
                profiles={profiles}
                bomId={bomId}
                revisionId={revision?.revision_id}
                onRefresh={fetchBom}
                isDraft={canEdit}
                onClearSelection={handleClearMultiSelect}
                onBusy={setBomBusy}
              />
            ) : detailTab === 'detail' ? (
              <DetailPanel line={selectedLineData} profiles={profiles} onUpdate={updateLine} onRemove={removeLine}
                budgetContext={{ budget_target: bom.budget_target || 0, current_bom_cost: revision?.cost_total || 0 }}
                bomId={bomId} revisionId={revision?.revision_id} onRefresh={fetchBom}
                isDraft={canEdit}
                allLines={lines}
                onBusy={setBomBusy}
                onProfileCreated={(p) => setProfiles(prev => [...prev, p])} />
            ) : (
              <ActivityFeedPanel
                bomId={bomId}
                revisionId={revision?.revision_id}
                bomName={bom.product_name}
                bomNumber={bom.bom_number}
                revisionLabel={revision?.revision_number}
              />
            )}
          </div>
        </div>
      </div>
      ) : activeLibraryTab === 'raw_material' ? (
      /* Raw Material Library — full content area */
      <div className="flex-1 overflow-hidden">
        <RawMaterialLibraryPage embedded bomTheme={theme}
          bomContext={bom && revision ? { bomId: bom.bom_id, revisionId: revision.revision_id, lineId: selectedLine, lineMakeBuy: selectedLineData?.make_buy, linePartNumber: selectedLineData?.part_number, isDraft: canEdit } : null}
          selectedLineIds={selectedLineIds}
          onMaterialApplied={fetchBom} />
      </div>
      ) : activeLibraryTab === 'fasteners' ? (
      /* Fasteners & Hardware Library — full content area */
      <div className="flex-1 overflow-hidden">
        <FastenersLibraryPage embedded bomTheme={theme} />
      </div>
      ) : activeLibraryTab === 'fabrication' ? (
      /* Fabrication Processes Library */
      <div className="flex-1 overflow-hidden">
        <ProcessLibraryPage domain="fabrication" onBack={() => setActiveLibraryTab('product_builder')}
          bomContext={bom && revision ? { bomId: bom.bom_id, revisionId: revision.revision_id, lineId: selectedLine, lineMakeBuy: selectedLineData?.make_buy, isDraft: canEdit } : null}
          onProcessApplied={fetchBom} />
      </div>
      ) : activeLibraryTab === 'surface_treatment' ? (
      /* Surface Treatment Library */
      <div className="flex-1 overflow-hidden">
        <ProcessLibraryPage domain="surface_treatment" onBack={() => setActiveLibraryTab('product_builder')}
          bomContext={bom && revision ? { bomId: bom.bom_id, revisionId: revision.revision_id, lineId: selectedLine, lineMakeBuy: selectedLineData?.make_buy, isDraft: canEdit } : null}
          onProcessApplied={fetchBom} />
      </div>
      ) : activeLibraryTab === 'purchase_parts' ? (
      /* Parts Catalog */
      <div className="flex-1 overflow-hidden">
        <PartsListPage />
      </div>
      ) : (
      /* Coming Soon Libraries */
      <div className="flex-1 overflow-hidden relative">
        <ComingSoonLibrary tabId={activeLibraryTab} onBack={() => setActiveLibraryTab('product_builder')} />
      </div>
      )}

      {/* Status Bar / Library Tabs Footer */}
      <ProductBuilderFooter
        activeTab={activeLibraryTab}
        onTabChange={setActiveLibraryTab}
        statusLeft={
          <span>
            {activeLibraryTab === 'product_builder' ? (
              <>
                {isMultiSelectActive
                  ? <span className="text-cyan-400">{selectedLineIds.size} parts selected for batch operations</span>
                  : selectedLineData
                    ? `Selected: ${selectedLineData.is_assembly ? '◆' : selectedLineData.make_buy === 'Make' ? '■' : '●'} ${selectedLineData.is_assembly ? selectedLineData.assembly_number : selectedLineData.part_number} (Level ${selectedLineData.level_depth})`
                    : 'Click a part in the tree to see details'
                }
                {insertionLabel && <span className="text-cyan-400 ml-3">│ Adding to: ◆ {insertionLabel}</span>}
              </>
            ) : null}
          </span>
        }
        statusRight={
          activeLibraryTab === 'product_builder'
            ? <span>Depth: {maxDepth} levels │ Auto-saved</span>
            : null
        }
      />
    </div>
    </DragProvider>
  );
};

const BomEditorPage = () => (
  <BomThemeProvider>
    <BomEditorPageInner />
  </BomThemeProvider>
);

export default BomEditorPage;
